import { Location } from '@angular/common';
import { Inject, Injectable, Injector, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Select, Store } from '@ngxs/store';
import * as moment from 'moment';
import { Observable, Subject, Subscription, from, of } from 'rxjs';
import { catchError, finalize, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { DynamicContentModalComponent } from '@Work/Accounts/proprietary/dynamic-content-outlet/dynamic-content-modal.component';
import { LocationDeleteShowRelatedArticlesDialogComponent } from '@Work/Accounts/proprietary/pages/contract-page/dialogs/location-delete-dialog/location-delete-show-related-articles-dialog.component';
import { AppService } from '@Core/services/proprietary/app.service';
import { ConfirmationDialogService } from '@Core/services/proprietary/confirmation-dialog/confirmation-dialog.service';
import { DataService } from '@Core/services/proprietary/data.service';
import { DataSourceStatus, Datasource } from '@Core/services/proprietary/datasource';
import { ModalFocusService } from '@Core/services/proprietary/modal-focus.service';
import { ObjectRuleService } from '@Core/services/proprietary/object-rule/object-rule.service';
import { RatingService } from '@Core/services/proprietary/rating.service';
import { SearchService } from '@Core/services/proprietary/search.service';
import { SearchResultType, SearchType } from '@Core/services/proprietary/search.types';
import { EditStatusType } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep, groupBy } from 'src/ep-utils';
import { InterfaceObjectService } from '../interface/interface-object.service';
import { PageObjectService } from '../page/page-object.service';
import { LockObject, LockObjectCodeType, LockSubType, UserDefinedValidation } from '../page/page-object.types';
import { EventType as PageEventType, PageState, SectionNavClassType } from '../page/page.state';
import { ProductLineConfigObjectService } from '../productline-config/productline-config-object.service';
import { ProductLineConfigObjectState } from '../productline-config/productline-config-object.state';
import { SubmissionWizardObjectState } from '../submission-wizard/submission-wizard-object.state';
import { TaskObjectService } from '../task/task-object.service';
import { UserDefinedObjectState } from '../user-defined/user-defined-object.state';
import { UserDefinedParentType, UserDefinedTableConfig } from '../user-defined/user-defined-object.types';
import { AddAddress, CloneAddress, DeleteAddress, RestoreAddress, UpdateAddress } from './address.actions';
import {
    AddArticle,
    AddProductsToArticle,
    AddSubArticle,
    AddVehicleDriverSubArticle,
    CloneArticle,
    CloneSubArticle,
    DeleteArticle,
    DeleteSubArticle,
    RestoreArticle,
    RestoreSubArticle,
    SetCurrentArticle,
    SetPrimaryPerilProduct,
    UpdateArticle,
    UpdateSubArticle,
    UpdateVehicleDriverSubArticle
} from './article.actions';
import { ArticleState } from './article.state';
import {
    AddAttachment,
    CloneAttachment,
    DeleteAttachment,
    RestoreAttachment,
    UpdateAttachment,
    UpdateAttachmentDocumentData
} from './attachment.actions';
import { AddCondition, DeleteCondition, RestoreCondition, UpdateCondition } from './condition.actions';
import {
    AddDefaultLocation,
    InitializeTransaction,
    LinkProfileToArticle,
    PrepareRequest,
    ResetState,
    SetContractObjectState
} from './contract-object.actions';
import { denormalize, normalize, rootSchema } from './contract-object.normalize';
import { ContractObjectState } from './contract-object.state';
import {
    ArticleNodeType,
    ContractProcTemplateActionTypes,
    ContractProcTemplateResultTypes,
    CostType,
    EntityNodeType,
    EventType,
    FormDocPackageTypes,
    PartyNodeType,
    ProcObjectCodeType,
    SubArticleNodeType,
    TransactionNodeType
} from './contract-object.types';
import { UpdateContractProcedure } from './contract-procedure.actions';
import { ContractProcedureState } from './contract-procedure.state';
import { SetBillToEntity, UpdateContract } from './contract.actions';
import { ContractState } from './contract.state';
import { AddConsumer, AddThirdPartyEntity, DeleteThirdPartyEntity, UpdateEntity } from './entity.actions';
import { EntityState } from './entity.state';
import { AddFactor, CloneFactor, DeleteFactor, RestoreFactor, UpdateFactor } from './factor.actions';
import { AddForm, CloneForm, DeleteForm, RestoreForm, UpdateForm } from './form.actions';
import { AddInterest, CloneInterest, DeleteInterest, RestoreInterest, UpdateInterest } from './interest.actions';
import { InterestState } from './interest.state';
import { AddLine, DeleteLine, SetPrimaryLine, UpdateLine } from './line.actions';
import { LineState } from './line.state';
import { AddLocation, CloneLocation, RestoreLocation, SetCurrentLocation, UpdateLocation } from './location.actions';
import { LocationState } from './location.state';
import { AddName, CloneName, DeleteName, RestoreName, UpdateName } from './name.actions';
import {
    AddParty,
    ClearParty,
    CloneParty,
    DeleteParty,
    RestoreParty,
    SetTPBillToEntity,
    UpdateParty,
    UpdatePartyAgreement
} from './party.actions';
import { PartyState } from './party.state';
import { AddPhone, ClonePhone, DeletePhone, RestorePhone, UpdatePhone } from './phone.actions';
import {
    AddLossHistory,
    AddPriorHistory,
    DeleteLossHistory,
    DeletePriorHistory,
    RestoreLossHistory,
    RestorePriorHistory,
    UpdateLossHistory,
    UpdatePriorHistory
} from './prior-history.actions';
import {
    DeleteProduct,
    RemoveSharedProduct,
    RestoreProduct,
    SelectAvailableProduct,
    UpdateProduct
} from './product.actions';
import { ProductState } from './product.state';
import { AddProfile, CloneProfile, DeleteProfile, RestoreProfile, UpdateProfile } from './profile.actions';
import {
    AddSubLocation,
    CloneSubLocation,
    DeleteSubLocation,
    OnLocationDeleted,
    RestoreSubLocation,
    SetCurrentSubLocation,
    SetCurrentSubLocationParent,
    UpdateSubLocation
} from './sub-location.actions';
import { SubLocationState } from './sub-location.state';
import {
    AddTeamMember,
    CloneTeamMember,
    DeleteTeamMember,
    RestoreTeamMember,
    UpdateTeamMember
} from './team-member.actions';
import { ContractPageView } from '@Work/Accounts/proprietary/pages/contract-page/contract-page.views';

@Injectable()
export class ContractObjectService implements OnDestroy {
    dataService: DataService;
    dsProcess: Datasource;
    dsGet: Datasource;
    dsGetLoadValidate: Datasource;
    dsOpenProcs: Datasource;
    dsProductSet: Datasource;
    dsAgmtTerm: Datasource;
    dsProcessRating: Datasource;
    dsGetRatingWorkspace: Datasource;
    dsRatingWorkspaceProcess: Datasource;
    dsContractStateValidation: Datasource;
    dsConsumer: Datasource

    private dsGetStatusSub: Subscription;
    private dsProcessStatusSub: Subscription;
    private dsProcessRatingStatusSub: Subscription;
    private dsGetRatingWorkspaceStatusSub: Subscription;
    private dsRatingWorkspaceProcessSub: Subscription;
    private templateStatusSub: Subscription;
    private dsOpenProcsStatusSub: Subscription;
    private dsGetLoadValidateStatusSub: Subscription;
    private dsProductSetSub: Subscription;
    private dsAgmtTermSub: Subscription;
    private dsContractStateValidationSub: Subscription;

    private transactionType: TransactionNodeType = TransactionNodeType.Contract;
    private objectID: any = null;
    private contractID: any = null;
    private pageID: any = null;
    private pageViewID: any = null;
    public sectionsBuilt = false;
    public wizardInUse = false;
    public wizardProcessReturnValue = new Subject();
    public agmtValidationError = false;
    public wizardSectionID = null;
    private wizardPageViewID: any = null;
    private lineData: any = [];
    private templateCriteria: any = {};
    private scheduledProcData: any = {};
    private reprocessTran: any = null;
    private lobChanged = false;
    public dataLoaded = new Subject();
    public launchRating: boolean = false;
    public LocationDeleteRelatedArticlesUnsubscribe$ = new Subject();
    modalRef: NgbModalRef;

    @Select(LocationState.currentLocation)
    location$: Observable<any>;
    locationSub: Subscription;
    pageViewSub: Subscription;

    subLocationSub: Subscription;
    currentSubLoc: any;

    @Select(ArticleState.currentArticle)
    article$: Observable<any>;
    articleSub: Subscription;

    manualEditModeSub: Subscription;
    selectExistingTaskSub: Subscription;

    interfaceCompleteSub: Subscription;

    initialDataLoaded = false;
    designerModeSectionData = {};

    constructor(
        private store: Store,
        private app: AppService,
        private pageOS: PageObjectService,
        private modalService: NgbModal,
        private modalFocusService: ModalFocusService,
        private injector: Injector,
        private location: Location,
        private taskOS: TaskObjectService,
        private objectRuleService: ObjectRuleService,
        private search: SearchService,
        private confirmationDialog: ConfirmationDialogService,
        private productLineConfigOS: ProductLineConfigObjectService,
        private ratingService: RatingService,
        private router: Router,
        //private sessionIdle: SessionIdleService,
        private interfaceOS: InterfaceObjectService,
        // @Inject('BASE_URL') private baseUrl: string
    ) { }

    goBack() {
        this.location.back();
    }

    ngOnDestroy() {
        this.reset();
    }

    reloadObject() {
        if (this.objectID && this.objectID != '' && this.objectID != '0' && this.objectID != '-1') {
            this.loadObject(this.objectID);
        }
    }

    //Links object to the calling windows dataservice
    setDataService(dataService: DataService, transactionType: TransactionNodeType = TransactionNodeType.Contract) {
        this.dataService = dataService;
        this.dsGet = this.dataService.addNewDatasource(transactionType);
        this.dsProcess = this.dataService.addNewDatasource(transactionType);
        this.dsProcessRating = this.dataService.addNewDatasource('SpreadsheetRating');
        this.dsOpenProcs = this.dataService.addNewDatasource('DBQuery');
        this.dsProductSet = this.dataService.addNewDatasource('DBQuery');
        this.dsGetRatingWorkspace = this.dataService.addNewDatasource('Workspace');
        this.dsRatingWorkspaceProcess = this.dataService.addNewDatasource('Workspace');
        this.dsContractStateValidation = this.dataService.addNewDatasource('DBQuery');
        this.dsAgmtTerm = this.dataService.addNewDatasource('BrokerAgreementTerm');
        this.transactionType = transactionType;
        this.dsConsumer = this.dataService.addNewDatasource('Consumer');
        this.createSubscriptions();

        this.ratingService.setDataService(dataService);
    }

    createSubscriptions() {
        this.pageViewSub = this.store.select(PageState.currentPageView).subscribe((view) => {
            //this.pageViewChanged(view);
        });

        this.manualEditModeSub = this.pageOS.manualEditTracker.subscribe(() => {
            this.manualEditMode();
        });
        this.selectExistingTaskSub = this.taskOS.selectExistingTaskTracker.subscribe(() => {
            this.existingTaskSelected();
        });
    }

    manualEditMode() {
        if (this.transactionType == TransactionNodeType.Contract) {
            if (
                this.dsGet.objectNode.Revision_Status != 'InProcess' &&
                this.app.getUserOptionValue('Contract_Admin_Ind') == 'Y'
            ) {
                this.app.showAlert({
                    title: 'Warning',
                    message: 'Use CAUTION when editing With Contract Administrator access.'
                });
            }
        }
        this.reloadObject();
    }

    existingTaskSelected() {
        if (!this.pageOS.isLocked()) {
            this.reloadObject();
        }
    }

    load(pageID: any) {
        if (!this.dataService) {
            throw 'setDataService() must be called prior to loadObject()! [contract-object.service]';
        }

        this.pageViewID = null;
        this.pageID = pageID;
        this.initialDataLoaded = false;

        let procID = this.app.pageParams.getParameter(pageID, 'Proc_ID');
        let contractID = this.app.pageParams.getParameter(pageID, 'Contract_ID');
        let objectCode = this.app.pageParams.getParameter(pageID, 'Object_Code');

        if (this.transactionType == TransactionNodeType.Contract) {
            if (contractID && contractID > '') {
                this.loadObject(contractID);
            } else {
                this.pageOS.newObject();
                this.search.openDialog(
                    SearchType.Contract,
                    this.selectContractResult.bind(this),
                    {
                        View: 'Full',
                        json: 'CT2',
                        Reference_Options: 'Desc.All;Entity_Name.All',
                        Range: 'ALL',
                        Contract_Set_Status: 'ALL',
                        Revision_Mode: 'ALL'
                    },
                    { autoSearch: false, allowClear: false, allowMultiSelect: false }
                );
            }
        } else {
            if (procID && procID > '') {
                this.loadObject(procID);
            } else {
                this.pageOS.newObject();
                // Sure MGA doesn't use ApplicationTemplate. We will always use ClearanceProcess for new Applications.
                if (this.transactionType != TransactionNodeType.Application) {
                    if (contractID && contractID > '') {
                        switch (this.transactionType) {
                            case TransactionNodeType.Bind:
                            case TransactionNodeType.ContractDisable:
                            case TransactionNodeType.Issue:
                            case TransactionNodeType.Market:
                            case TransactionNodeType.Quote:
                                this.templateCriteria = { Contract_ID: contractID };
                                break;

                            default:
                                this.templateCriteria = { Contract_Set_ID: contractID };
                        }
                        this.preTemplateChecks();
                    } else {
                        if (!objectCode || objectCode == '') {
                            //Back up, incase the Object_Code isn't in the page parms.
                            objectCode = ProcObjectCodeType[this.transactionType];
                        }

                        this.search.openDialog(
                            SearchType.Contract,
                            this.selectContractResult.bind(this),
                            {
                                Valid_For_Procedure: objectCode
                            },
                            { autoSearch: false, allowClear: false, allowMultiSelect: false }
                        );
                    }
                }
            }
        }
    }

    loadContract(contractID: any) {
        if (!this.dataService) {
            throw 'setDataService() must be called prior to loadObject()! [contract-object.service]';
        }


        //let procID = this.app.pageParams.getParameter(pageID, 'Proc_ID');
        //let contractID = this.app.pageParams.getParameter(pageID, 'Contract_ID');
        //let objectCode = this.app.pageParams.getParameter(pageID, 'Object_Code');
        //let designerMode = this.app.pageParams.getParameter(pageID, 'Designer_Mode');
        this.transactionType = TransactionNodeType.Contract;
        this.loadObject(contractID);


    }

    openTemplateDialog() {
        this.templateDialog(this.pageID, this.templateDialogResults.bind(this));
    }

    templateDialogResults(result: ContractProcTemplateResultTypes) {
        if (result.action == 'proceed') {
            let criteria: any = {
                Contract_Set_ID: result.data.Contract_Set_ID
            };

            switch (this.transactionType) {
                case TransactionNodeType.Cancel:
                    criteria.Cancel_Date = result.data.Cancel_Date;
                    criteria.Cancel_Method = result.data.Cancel_Method;
                    break;

                case TransactionNodeType.Endorsement:
                    criteria.Endorse_Eff_Date = result.data.Endorse_Date;
                    break;

                case TransactionNodeType.ExtendTerm:
                    criteria.New_Exp_Date = result.data.New_Exp_Date;
                    break;

                case TransactionNodeType.Renewal:
                    criteria.Renewal_Eff_Date = result.data.Renewal_Eff_Date;
                    break;

                case TransactionNodeType.SelfAudit:
                    criteria.Reporting_Date = result.data.Reporting_Date;
                    break;
            }
            this.templateCriteria = criteria;
            this.getTemplate();
        } else {
            this.location.back();
        }
    }

    async preTemplateChecks() {
        let contractID = this.templateCriteria.Contract_ID || this.templateCriteria.Contract_Set_ID;

        if (await this.contractStateValidation(contractID, ProcObjectCodeType[this.transactionType])) {
            if (this.dsContractStateValidation.objectNodes[0].ContractValidation != 'Valid') {
                this.app.showAlert({
                    title: 'Error',
                    message: this.dsContractStateValidation.objectNodes[0].ContractValidation
                });
                this.location.back();
            }
        }

        //This check needs to be last. If any others are added they need to occur prior to this...
        if (await this.openProceduresExist(contractID, ProcObjectCodeType[this.transactionType])) {
            this.selectOpenProcDialog();
        } else {
            this.startTemplate();
        }
    }

    async startTemplate() {
        let contractID = this.templateCriteria.Contract_ID || this.templateCriteria.Contract_Set_ID;

        switch (this.transactionType) {
            case TransactionNodeType.Endorsement:
            case TransactionNodeType.ExtendTerm:
            case TransactionNodeType.Renewal:
            case TransactionNodeType.SelfAudit:
                // These procedures have a special dialog for building the Template transaction.
                //Procedure Template Edit Panels get the Contract_ID from pageParms.
                this.app.pageParams.setParameter(this.pageID, 'Contract_ID', contractID);
                this.openTemplateDialog();
                break;

            case TransactionNodeType.Cancel:
                //Procedure Template Edit Panels get the Contract_ID from pageParms.
                this.app.pageParams.setParameter(this.pageID, 'Contract_ID', contractID);

                if (await this.hasScheduledCancel(contractID)) {
                    this.openScheduledCancelDialog();
                } else {
                    this.openTemplateDialog();
                }

                break;

            default:
                this.getTemplate();
                break;
        }
    }

    getTemplate() {
        if (this.templateStatusSub) {
            this.templateStatusSub.unsubscribe();
        }
        this.templateStatusSub = this.dsGet.status$.subscribe((x) => {
            this.templateStatusUpdated(x);
        });

        this.dsGet.getTemplate({
            ...{ View: 'Full', json: 'CT2' },
            ...this.templateCriteria
        });
    }

    templateStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                if (this.templateStatusSub) {
                    this.templateStatusSub.unsubscribe();
                }
                this.newObject(this.transactionType);

                //Initialize Proc_ID as -1
                if (
                    this.dsGet &&
                    this.dsGet.objectNode &&
                    (!this.dsGet.objectNode.Proc_ID || this.dsGet.objectNode.Proc_ID == '0')
                ) {
                    this.dsGet.objectNode.Proc_ID = '-1';
                }

                //Procecdure Specific "Pre-Load" logic.
                switch (this.transactionType) {
                    case TransactionNodeType.Bind:
                        let binderNum = this.dsGet.objectNode.Binder_Number;
                        let contractNum = this.dsGet.objectNode.Contract.Contract_Num;
                        if (!binderNum || binderNum == '') {
                            this.dsGet.objectNode.Binder_Number = contractNum;
                        }
                        break;
                    //Add other procedure specific logic here.
                }

                this.loadData(this.dsGet.data, true);
                break;

            case DataSourceStatus.Error:
                if (this.templateStatusSub) {
                    this.templateStatusSub.unsubscribe();
                }
                break;
        }
    }

    selectContractResult(result: SearchResultType) {
        if (result.action == 'select') {
            switch (this.transactionType) {
                case TransactionNodeType.Contract:
                    let contractID = result.data[0].Contract_ID;
                    this.loadObject(contractID);
                    break;

                case TransactionNodeType.Bind:
                case TransactionNodeType.ContractDisable:
                case TransactionNodeType.Issue:
                case TransactionNodeType.Market:
                case TransactionNodeType.Quote:
                    this.templateCriteria = { Contract_ID: result.data[0].Contract_ID };
                    break;

                default:
                    this.templateCriteria = { Contract_Set_ID: result.data[0].Contract_Set_ID };
                    break;
            }
            if (this.transactionType != TransactionNodeType.Contract) {
                this.preTemplateChecks();
            }
        } else {
            this.location.back();
        }
    }

    async loadData(data: any, template: boolean = false) {
        this.pageOS.setPristine();
        if (this.transactionType == TransactionNodeType.Contract) {
            if (
                this.dsGet.objectNode.Revision_Status != 'InProcess' &&
                this.app.getUserOptionValue('Contract_Admin_Ind') == 'N'
            ) {
                this.pageOS.setProtectedMode(true);
            }
        } else {
            if (this.dsGet.objectNode.Proc_Status == 'A' || this.dsGet.objectNode.Proc_Status == 'R') {
                this.pageOS.setProtectedMode(true);
            }
        }

        //Locking
        if (!this.wizardInUse) {
            //Don't attempt to Lock if ContractOS being used by Wizard (QuoteSet or MarketSet)

            let lockObjects: LockObject[] = [];
            let contractNode: any;
            if (this.transactionType == TransactionNodeType.Contract) {
                contractNode = this.dsGet.objectNode;
            } else {
                contractNode = this.dsGet.objectNode.Contract;
            }
            lockObjects.push({
                Object_ID: contractNode.Contract_Set_ID,
                Object_Code: LockObjectCodeType.Contract,
                Lock_Sub_Type: LockSubType.Contract
            });

            if (
                !template &&
                (this.transactionType == TransactionNodeType.Renewal ||
                    this.transactionType == TransactionNodeType.Rewrite ||
                    this.transactionType == TransactionNodeType.Reissue)
            ) {
                lockObjects.push({
                    Object_ID: this.dsGet.objectNode.Previous_Contract.Contract_Set_ID,
                    Object_Code: LockObjectCodeType.Contract,
                    Lock_Sub_Type: LockSubType.Contract
                });
            }

            await this.pageOS.lockObject(lockObjects);
        }

        //Special Case for Issue if already Bound.
        if (this.transactionType == TransactionNodeType.Issue) {
            let tempContract = this.dsGet.objectNode.Contract;
            if (tempContract && tempContract.Revision_Status && tempContract.Revision_Status.toLowerCase() != 'inprocess') {
                let alterMethod = this.app.getBookOptionValue('Alter_Contract_Method');
                if (alterMethod && alterMethod === 'E') {
                    this.pageOS.setAllSectionsReadOnly(true, ['ContractIssueSectionComponent']);
                }
            }
        }

        // Specical for Reinstate - Once Submitted.
        if (this.transactionType == TransactionNodeType.Reinstate) {
            if (this.dsGet.objectNode && this.dsGet.objectNode.Proc_Status && this.dsGet.objectNode.Proc_Status == 'S') {
                this.pageOS.setAllSectionsReadOnly(true, ['ContractReinstateSectionComponent']);
            }
        }

        if (this.dsProcess.error == true) {
            this.dsProcess.reset(); //clear process datastore (resets error messages)
        }

        if (!this.wizardInUse) {
            //Skip if contractOS being used by Quote or Market Wizard.
            this.setValidEvents();
        }
        await this.prepData();
        let normalizedData = normalize(data, rootSchema);

        this.store.dispatch(new SetContractObjectState(normalizedData.entities)).subscribe((action) => {
            this.postLoadData(template);
        });
    }

    async prepData() {
        if (this.dsGet && this.dsGet.objectNode && this.dsGet._status == DataSourceStatus.Ready) {
            // Pass Line_ID from Contract_Line into Contract_Detail and Contract_Party nodes.
            // Needed for normalizing process strategy to then pass Line_ID into the various Party and Article nodes
            let contractNode: any;

            if (this.transactionType == TransactionNodeType.Contract) {
                contractNode = this.dsGet.objectNode;
            } else {
                contractNode = this.dsGet.objectNode.Contract;
            }

            let idx = -1;

            if (contractNode) {
                await this.productLineConfigOS.loadObjects(contractNode.Contract_Line);

                for (var i in contractNode.Contract_Line) {
                    if (this.lineData && this.lineData.length > 0) {
                        idx = -1;
                        // Find the Index of lineData with with the same Line_Code. Set a New_Line_ID property.
                        // Later on, having a Line_ID and New_Line_ID that aren't the same tiggers the section Data to be updated.
                        idx = this.lineData.map((line) => line.Line_Code).indexOf(contractNode.Contract_Line[i].Line_Code);
                        if (idx > -1) {
                            this.lineData[idx].New_Line_ID = contractNode.Contract_Line[i].Line_ID;
                        }
                    }

                    if (contractNode.Contract_Line[i].hasOwnProperty('Contract_Party')) {
                        if (contractNode.Contract_Line[i].Contract_Party == '') {
                            contractNode.Contract_Line[i].Contract_Party = {};
                        }
                        contractNode.Contract_Line[i].Contract_Party.Line_ID = contractNode.Contract_Line[i].Line_ID;
                        contractNode.Contract_Line[i].Contract_Party.Line_Code = contractNode.Contract_Line[i].Line_Code;
                    }
                    if (contractNode.Contract_Line[i].hasOwnProperty('Contract_Detail')) {
                        if (contractNode.Contract_Line[i].Contract_Detail == '') {
                            contractNode.Contract_Line[i].Contract_Detail = {};
                        }
                        contractNode.Contract_Line[i].Contract_Detail.Line_ID = contractNode.Contract_Line[i].Line_ID;
                    }
                }

                if (this.lineData && this.lineData.length > 0) {
                    for (var i in this.lineData) {
                        if (
                            this.lineData[i].Line_ID &&
                            this.lineData[i].New_Line_ID &&
                            this.lineData[i].Line_ID != this.lineData[i].New_Line_ID
                        ) {
                            this.pageOS.updateSectionData(
                                { Line_ID: this.lineData[i].Line_ID },
                                { Line_ID: this.lineData[i].New_Line_ID }
                            );
                        }
                    }
                }
            }
        }
    }

    async postLoadData(template: boolean) {
        //If a CancelTemplate was loaded, do an autoSave to calculate the Net Change.
        if (template && this.transactionType == TransactionNodeType.Cancel) {
            this.autoCreateCancel();
            return;
        }
        await this.pageOS.retrieveProductLineArticle();
        let forcePageViewRefresh = !this.initialDataLoaded || this.lobChanged;
        this.initialDataLoaded = true;
        this.lobChanged = false;

        if (this.wizardInUse) {
            let availableSections = this.store.selectSnapshot(
                SubmissionWizardObjectState.currentWizardPageView
            ).AvailableSections;

            if (availableSections && availableSections.length > 0) {
                let sections = this.buildSections(availableSections);
                if (sections && sections.length > 0) {
                    // The QSW and MSW add the Sections as children to the Option Section.
                    this.pageOS.replaceChildSections(this.wizardSectionID, sections);
                }
            }
        } else {
            let currentPageView = this.store.selectSnapshot(PageState.currentPageView);
            if (currentPageView) {
                this.pageViewChanged(currentPageView, forcePageViewRefresh); // trigger navigator refresh if pageView has not been built
            }
        }

        this.checkForPrimaryPerilProduct();
        this.dataLoaded.next(null);

        if (this.launchRating) {
            this.beginRating();
        }
    }

    private autoCreateCancel() {
        if (this.scheduledProcData && this.scheduledProcData.Proc_ID) {
            //Cancel_Date and Cancel_Method already added when calling the Template transaction the first time.
            let cancelProc = this.store.selectSnapshot(ContractProcedureState.procedure);

            cancelProc = cloneDeep(cancelProc);

            cancelProc.CancSched_Proc_ID = this.scheduledProcData.Proc_ID;
            if (this.scheduledProcData.Annotation) {
                cancelProc.Annotation = this.scheduledProcData.Annotation;
            }
            if (this.scheduledProcData.Cancel_Reason) {
                cancelProc.Cancel_Reason = this.scheduledProcData.Cancel_Reason;
            }
            if (this.scheduledProcData.Cancel_Auditable_Ind) {
                cancelProc.Cancel_Auditable_Ind = this.scheduledProcData.Cancel_Auditable_Ind;
            }

            this.store.dispatch(new UpdateContractProcedure(cancelProc)).subscribe((action) => {
                this.process(EventType.Create, { Auto_Calculate_Net_Change: 'Y' });
            });
        } else {
            this.process(EventType.Create, { Auto_Calculate_Net_Change: 'Y' });
        }
    }

    buildSections(availableSections: any): any {
        if (!this.sectionsBuilt) {
            this.sectionsBuilt = true;
            let sections: any = [];
            let childSections: any = [];
            let contractNode: any;
            let productLineArticleConfig = this.pageOS.getProductLineArticleConfig();

            if (this.transactionType == TransactionNodeType.Contract) {
                contractNode = this.dsGet.objectNode;
            } else {
                contractNode = this.dsGet.objectNode.Contract;
            }


            for (var sectionIdx in availableSections) {
                if (
                    availableSections[sectionIdx].Section_Group &&
                    availableSections[sectionIdx].Section_Group == 'GENERAL' &&
                    (availableSections[sectionIdx].Section_Key?.toUpperCase() == contractNode.Line_Code?.toUpperCase() ||
                        availableSections[sectionIdx].Section_Key == '<ALL>')
                ) {
                    sections.push(availableSections[sectionIdx]);
                }
            }

            for (var i in contractNode.Contract_Line) {
                childSections = []; //Clear for each loop.
                for (var sectionIdx in availableSections) {
                    let addSection: boolean = false;
                    let findIdx = -1;
                    if (
                        availableSections[sectionIdx].Section_Group &&
                        availableSections[sectionIdx].Section_Group == 'LINE' &&
                        (availableSections[sectionIdx].Section_Key?.toUpperCase() ==
                            contractNode.Contract_Line[i].Line_Code?.toUpperCase() ||
                            availableSections[sectionIdx].Section_Key == '<ALL>')
                    ) {
                        if (
                            availableSections[sectionIdx].Section_Key == '<ALL>' ||
                            ['LINE', 'MISCAR', 'ERP', 'FORM', 'FACTOR'].includes(
                                availableSections[sectionIdx].Article_Type?.toUpperCase()
                            )
                        ) {
                            addSection = true;
                        } else {
                            //Check Product Line Article mapping
                            findIdx = productLineArticleConfig.findIndex(
                                (idxConfig) =>
                                    idxConfig.Article_Type?.toUpperCase() == availableSections[sectionIdx].Article_Type?.toUpperCase() &&
                                    idxConfig.Line_Code?.toUpperCase() == availableSections[sectionIdx].Section_Key?.toUpperCase()
                            );

                            if (findIdx > -1) {
                                addSection = true;
                            }
                        }

                        if (addSection) {
                            childSections.push({
                                ...availableSections[sectionIdx],
                                ...{
                                    Data: {
                                        Line_ID: contractNode.Contract_Line[i].Line_ID,
                                        Line_Code: contractNode.Contract_Line[i].Line_Code
                                    }
                                }
                            });
                        }
                    }
                }

                if (childSections.length > 0) {
                    //Only add Section for Line_Code if at least one child section is defined.
                    sections.push({
                        Title: contractNode.Contract_Line[i].Line_Code_Desc,
                        Navigator_Component_Type: 'ContractLineNavigatorComponent',
                        Data: {
                            Line_ID: contractNode.Contract_Line[i].Line_ID,
                            Line_Code: contractNode.Contract_Line[i].Line_Code
                        },
                        Navigator_Class: SectionNavClassType.Normal,
                        Indent_Children: true,
                        Child_Sections: childSections
                    });
                }
            }

            for (var sectionIdx in availableSections) {
                if (
                    availableSections[sectionIdx].Section_Group &&
                    availableSections[sectionIdx].Section_Group == 'SUMMARY' &&
                    (availableSections[sectionIdx].Section_Key?.toUpperCase() == contractNode.Line_Code?.toUpperCase() ||
                        availableSections[sectionIdx].Section_Key == '<ALL>')
                ) {
                    sections.push(availableSections[sectionIdx]);
                }
            }

            return sections;
        }
    }

    pageViewChanged(view: any, forceRefresh: boolean = false) {
        // Called when pageview changed or data is loaded -- sets up the section navigator with dynamic sections
        // Will not setup sections unless data has already been retrieved and page views have been populated
        // Since both data retrieve and page view retrive are aysnchronous they can complete in any order

        this.sectionsBuilt = false;
        let availableSections = this.store.selectSnapshot(PageState.currentPageView).AvailableSections;
        let sections = this.buildSections(availableSections);
        if (sections && sections.length > 0) {
            this.pageOS.setPageViewSections(sections);
        }
    }

    wizardOptionPageViewChanged(viewID: any) {
        if (this.wizardInUse) {
            if (viewID && this.wizardPageViewID != viewID) {
                this.wizardPageViewID = viewID;
                this.sectionsBuilt = false;
            }

            let availableSections = this.store.selectSnapshot(
                SubmissionWizardObjectState.currentWizardPageView
            ).AvailableSections;

            let sections = this.buildSections(availableSections);
            if (sections && sections.length > 0) {
                this.pageOS.replaceChildSections(this.wizardSectionID, sections);
            }
        }
    }

    loadObject(id: any) {
        if (!this.dataService) {
            throw 'setDataService() must be called prior to loadObject()! [contract-object.service]';
        }
        this.objectID = id;

        let getUnselectedForms = false;
        let getUnselectedConditions = false;

        if (this.dsProcess.tranNode && this.dsProcess.tranNode.Auto_Generate_Forms === 'Y') {
            getUnselectedForms = true;
        }

        if (this.dsProcess.tranNode && this.dsProcess.tranNode.Auto_Generate_Conditions === 'Y') {
            getUnselectedConditions = true;
        }

        this.dsGet.reset(); //clear process datastore (resets error messages)
        if (this.dsProcess.error == true) {
            this.dsProcess.reset(); //clear process datastore (resets error messages)
        }
        let criteria: any = { View: 'Full', json: 'CT2' };

        if (getUnselectedForms) {
            criteria.Include_Unselected_Forms = 'Y';
        }

        if (getUnselectedConditions) {
            criteria.Include_Unselected_Conditions = 'Y';
        }

        if (this.transactionType == TransactionNodeType.Contract) {
            criteria.Contract_ID = id;
        } else {
            criteria.Proc_ID = id;
        }
        this.dsGet.get(criteria, true, false);
        this.dsGetStatusSub = this.dsGet.status$.subscribe((x) => {
            this.dsGetStatusUpdated(x);
        });
    }

    resetState() {
        this.store.dispatch(new ResetState());
    }

    reset() {
        if (this.dsGetStatusSub) {
            this.dsGetStatusSub.unsubscribe();
        }
        if (this.dsGetRatingWorkspaceStatusSub) {
            this.dsGetRatingWorkspaceStatusSub.unsubscribe();
        }
        if (this.dsProcessStatusSub) {
            this.dsProcessStatusSub.unsubscribe();
        }
        if (this.dsProcessRatingStatusSub) {
            this.dsProcessRatingStatusSub.unsubscribe();
        }
        if (this.templateStatusSub) {
            this.templateStatusSub.unsubscribe();
        }
        if (this.locationSub) {
            this.locationSub.unsubscribe();
        }
        if (this.subLocationSub) {
            this.subLocationSub.unsubscribe();
        }
        if (this.articleSub) {
            this.articleSub.unsubscribe();
        }
        if (this.pageViewSub) {
            this.pageViewSub.unsubscribe();
        }
        if (this.dsOpenProcsStatusSub) {
            this.dsOpenProcsStatusSub.unsubscribe();
        }
        if (this.manualEditModeSub) {
            this.manualEditModeSub.unsubscribe();
        }
        if (this.selectExistingTaskSub) {
            this.selectExistingTaskSub.unsubscribe();
        }
        if (this.dsGetLoadValidateStatusSub) {
            this.dsGetLoadValidateStatusSub.unsubscribe();
        }
        if (this.dsProductSetSub) {
            this.dsProductSetSub.unsubscribe();
        }
        if (this.dsAgmtTermSub) {
            this.dsAgmtTermSub.unsubscribe();
        }
        if (this.interfaceCompleteSub) {
            this.interfaceCompleteSub.unsubscribe();
        }
        if (this.dsContractStateValidationSub) {
            this.dsContractStateValidationSub.unsubscribe();
        }
        if (this.dsGet) {
            this.dataService.deleteDatasource(this.dsGet);
        }
        if (this.dsGetRatingWorkspace) {
            this.dataService.deleteDatasource(this.dsGetRatingWorkspace);
        }
        if (this.dsProcess) {
            this.dataService.deleteDatasource(this.dsProcess);
        }
        if (this.dsOpenProcs) {
            this.dataService.deleteDatasource(this.dsOpenProcs);
        }
        if (this.dsGetLoadValidate) {
            this.dataService.deleteDatasource(this.dsGetLoadValidate);
        }
        if (this.dsProductSet) {
            this.dataService.deleteDatasource(this.dsProductSet);
        }
        if (this.dsAgmtTerm) {
            this.dataService.deleteDatasource(this.dsAgmtTerm);
        }
        if (this.dsContractStateValidation) {
            this.dataService.deleteDatasource(this.dsContractStateValidation);
        }

        this.resetState();
        this.objectID = null;
        this.contractID = null;
        this.pageID = null;
        this.sectionsBuilt = false;
        this.wizardInUse = false;
        this.wizardSectionID = null;
        this.templateCriteria = {};
        this.scheduledProcData = {};
        this.lineData = [];
        this.reprocessTran = null;
        this.initialDataLoaded = false;
        this.lobChanged = false;
        this.launchRating = false;
    }

    async deleteConfirmation() {
        return new Promise(async (resolve) => {
            this.confirmationDialog
                .confirm('Confirm Delete', 'Are you sure you want to delete?', 'Yes', 'No', 'sm')
                .then((confirmed) => {
                    resolve(confirmed);
                })
                .catch(() => {
                    resolve(false);
                });
        });
    }

    async process(event: EventType, tranOptions?: any) {
        //event is a single event
        //tranOptions are attributes to be added to the transaction node.   They are passed in as a json object with the key being the attribute name
        //e.g. {Auto_Calculate_Taxes: 'Y', Auto_Calculate_Fees: 'Y', Auto_Generate_Forms: 'Y', Auto_Select_Optional_Forms: 'Y'}
        try {
            if (!this.dataService) {
                throw 'setDataService() must be called prior to process()! [contract-object.service]';
            }

            //Verify page has Object_Lock.
            if (!this.pageOS.isLocked()) {
                this.pageOS.setProtectedMode(true);
                throw 'Contract must be locked prior to process()! [contract-object.service]';
            }

            if (this.transactionType == TransactionNodeType.Application) {
                await this.addDefaultLocation();
            }

            if (event == EventType.Delete) {
                let confirmed = await this.deleteConfirmation();
                if (confirmed !== true) {
                    return;
                }
            }
            if (event != EventType.Delete) {
                //validate contract and party info
                let errorMsg = this.validateContractSetup(event);
                if (errorMsg != '' && errorMsg != null) {
                    this.app.showAlert({
                        title: 'Validation Error',
                        message: errorMsg
                    });
                    throw 'One or more contract or contract party validation errors occurred! [contract-object.service]';
                }
                this.agmtValidationError = false;
                await this.validateAgreements();

                if (this.agmtValidationError) {
                    this.agmtValidationError = false;
                    this.app.showAlert({
                        title: 'Validation Error',
                        message:
                            'Invalid Contract Party Agreement(s)! The invalid party record(s) have been cleared, ' +
                            'please review/re-select Contract Broker or Supplier party record(s).'
                    });
                    throw 'Contract party agreement validation error occurred! [contract-object.service]';
                }
            }

            if (!this.validate(event)) {
                throw 'One or more validation errors occurred! [contract-object.service]';
            }

            if (this.transactionType == TransactionNodeType.Cancel && event == EventType.Accept) {
                if (this.app.getBookOptionValue('Allow_Future_Cancel_Accept_Ind') == 'N') {
                    let currentDateTime: moment.Moment = moment.parseZone(new Date(Date.now())).utc(true);
                    let cancelDate = moment(this.store.selectSnapshot(ContractProcedureState.procedure)?.Cancel_Date);
                    if (cancelDate.isAfter(currentDateTime)) {
                        this.app.showAlert({
                            title: 'Error',
                            message: 'Future Cancellations Cannot be Accepted!'
                        });
                        return;
                    }
                }
            }

            let hasOpenProcs = false;
            if (!this.wizardInUse) {
                //Open procedures not checked when called  by QuoteSet or MarketSet object service.
                if (event == EventType.Accept) {
                    let procID = this.objectID;
                    let contractSetID = this.contractID;
                    if (!contractSetID || contractSetID == '') {
                        contractSetID = this.store.selectSnapshot(ContractState.contract).Contract_Set_ID;
                    }

                    if (!procID || procID == '') {
                        procID = '-1';
                    }

                    let objectCode = this.app.pageParams.getParameter(this.pageID, 'Object_Code');
                    if (!objectCode || objectCode == '') {
                        //Back up, incase the Object_Code isn't in the page parms.
                        objectCode = ProcObjectCodeType[this.transactionType];
                    }
                    if (await this.hasOpenProcedures(contractSetID, objectCode, procID)) {
                        hasOpenProcs = true;
                    }
                }
            }

            if (hasOpenProcs) {
                this.confirmationDialog
                    .confirm(
                        'Open Contract Procedures',
                        'All other Contract Procedures for this contract must be rejected prior to Accepting this procedure.\n '
                        + 'Do you want to save now?',
                        'Yes',
                        'No',
                        'lg'
                    )
                    .then((confirmed) => {
                        if (confirmed) {
                            this.process(EventType.Update, tranOptions); //Recursive call, but with Update event.
                        } else {
                            return;
                        }
                    })
                    .catch(() => {
                        return;
                    });
            } else {
                this.prepareRequest(event, tranOptions);
                if (this.wizardInUse) {
                    this.wizardProcessReturnValue.next(true);
                }
            }
        } catch (error) {
            console.log(error);

            if (this.wizardInUse) {
                this.wizardProcessReturnValue.next(false);
            }
        }
    }

    async getAgreementTerms(criteria: any) {
        return new Promise(async (resolve) => {
            this.removeAgmtTermsSub();

            this.dsAgmtTerm.reset();
            if (criteria.Role_Type == 'BK') {
                this.dsAgmtTerm.objectType = 'BrokerAgreementTerm';
            } else if (criteria.Role_Type == 'SP') {
                this.dsAgmtTerm.objectType = 'SupplierAgreementTerm';
            }

            criteria.View = 'Standard';
            criteria.json = 'CT2';
            this.dsAgmtTerm.getCollection(criteria, true, true);
            this.dsAgmtTermSub = this.dsAgmtTerm.status$.subscribe((status) => {
                switch (status) {
                    case DataSourceStatus.Ready:
                        if (this.dsAgmtTerm.objectNodes.length > 0) {
                            this.removeAgmtTermsSub();
                            resolve(true);
                        } else {
                            resolve(false);
                        }
                        break;

                    case DataSourceStatus.Error:
                        this.removeAgmtTermsSub();
                        resolve(false);
                        break;
                }
            });
        });
    }

    removeAgmtTermsSub() {
        if (this.dsAgmtTermSub) {
            this.dsAgmtTermSub.unsubscribe();
        }
    }

    async validateAgreements() {
        return new Promise(async (resolve) => {
            let brokers = cloneDeep(this.store.selectSnapshot(PartyState.brokerParties));
            let suppliers = cloneDeep(this.store.selectSnapshot(PartyState.supplierParties));

            if (brokers?.length > 0) {
                for (var i in brokers) {
                    if (
                        brokers[i].agreementValidated == 'N' &&
                        brokers[i].Agreement?.Agmt_ID != '' &&
                        brokers[i].Agreement?.Agmt_Term_ID != ''
                    ) {
                        await this.validateAgreement(brokers[i], 'BK');
                    }
                }
            }
            if (suppliers?.length > 0) {
                for (var i in suppliers) {
                    if (
                        suppliers[i].agreementValidated == 'N' &&
                        suppliers[i].Agreement?.Agmt_ID != '' &&
                        suppliers[i].Agreement?.Agmt_Term_ID != ''
                    ) {
                        await this.validateAgreement(suppliers[i], 'SP');
                    }
                }
            }
            resolve(true);
        });
    }

    async validateAgreement(party: any, roleType: any) {
        return new Promise(async (resolve) => {
            if ((party.Edit_Status == 'A' || party.Edit_Status == 'C') && party.agreementValidated == 'N') {
                let effDate = this.store.selectSnapshot(ContractState.contractEffDate);
                let entityID: any;
                if (roleType == 'BK') {
                    entityID = party.Broker?.Entity_ID;
                } else {
                    entityID = party.Source_Supplier?.Entity_ID;
                }
                let criteria = {
                    State: party.State,
                    Line_Code: party.Line_Code,
                    Entity_ID: entityID,
                    Program: party.Program,
                    Range: 'Eff',
                    As_Of_Date: effDate,
                    Role_Type: roleType
                };
                await this.getAgreementTerms(criteria);
                let agreementTerms = this.dsAgmtTerm?.objectNodes;
                party.agreementValidated = 'Y';

                if (agreementTerms.length > 1) {
                    let found: boolean = false;
                    for (var j in agreementTerms) {
                        if (
                            party.Agreement?.Agmt_Term_ID == agreementTerms[j].Agmt_Term_ID &&
                            party.Agreement?.Agmt_ID == agreementTerms[j].Agmt_ID
                        ) {
                            found = true;
                            break;
                        }
                    }

                    if (!found) {
                        this.search.openDialog(
                            SearchType.PartyAgreement,
                            ((result) => {
                                if (result.action == 'select') {
                                    this.store.dispatch(new UpdatePartyAgreement(party, result.data[0])).subscribe((action) => {
                                        resolve(true);
                                    });
                                } else {
                                    this.agmtValidationError = true;
                                    this.store.dispatch(new ClearParty(party.Party_ID, true)).subscribe((action) => {
                                        resolve(true);
                                    });
                                }
                            }).bind(this),
                            criteria,
                            {
                                autoSearch: true,
                                allowClear: false,
                                allowMultiSelect: false
                            }
                        );
                    } else {
                        resolve(true);
                    }
                } else if (agreementTerms.length == 1) {
                    if (
                        !(
                            party.Agreement?.Agmt_Term_ID == agreementTerms[0].Agmt_Term_ID &&
                            party.Agreement?.Agmt_ID == agreementTerms[0].Agmt_ID
                        )
                    ) {
                        this.store.dispatch(new UpdatePartyAgreement(party, agreementTerms[0])).subscribe((action) => {
                            resolve(true);
                        });
                    } else {
                        resolve(true);
                    }
                } else {
                    this.agmtValidationError = true;
                    this.store.dispatch(new ClearParty(party.Party_ID, true)).subscribe((action) => {
                        resolve(true);
                    });
                }
            } else {
                resolve(true);
            }
        });
    }

    prepareRequest(event: EventType, tranOptions?: any) {
        let teminatingEvents = [EventType.Submit, EventType.Accept, EventType.Reject, EventType.Delete];

        this.store
            .dispatch(
                new PrepareRequest({
                    Lock_Mode: 'N',
                    Lock_GUID: this.pageOS.getCurrentLockGUID(),
                    Keep_Task_Open: this.pageOS.keepTaskOpen(event, teminatingEvents, false),
                    Event: event,
                    ...tranOptions
                })
            )
            .subscribe((action) => this.finishProcess(event));
    }

    finishProcess(event: EventType) {
        let stateEntities = this.store.selectSnapshot(ContractObjectState.entities);

        stateEntities = cloneDeep(stateEntities);

        if (event == EventType.Rate) {
            this.launchRating = true;
            stateEntities.Transaction[1].Event = event.replace('Rate', 'Update');
        }

        //TODO:  Change SDK to allow negative ID's  -- treat them as new and don't validate exisitence
        if (stateEntities.Procedure && stateEntities.Procedure['-1']) {
            delete stateEntities.Procedure['-1'].Proc_ID;
            stateEntities.Transaction[1].Event = event.replace('Update', 'Create');

            if (stateEntities.Transaction[1].Event == 'Delete') {
                //Nothing to Delete if prior to creation.
                this.pageOS.setPristine(); // Set to pristine so they user doesn't get prompted again about leaving w/ unsaved changes.
                this.goBack();
                return;
            }
        }

        if (this.transactionType == TransactionNodeType.ExtendTerm) {
            for (var i in stateEntities.Procedure) {
                if (stateEntities.Procedure[i].Total_Endorsement_Prem == '') {
                    delete stateEntities.Procedure[i].Total_Endorsement_Prem;
                }
            }
        }

        if (stateEntities.Contract && stateEntities.Contract['-1']) {
            delete stateEntities.Contract['-1'].Contract_ID;
        }

        for (var i in stateEntities.Entity) {
            if (i.slice(0, 1) == '-') {
                delete stateEntities.Entity[i].Entity_ID;
            }

            if (stateEntities.Entity[i].Entity_Type == 'O') {
                delete stateEntities.Entity[i].Indv_Name;
            }

            if (stateEntities.Entity[i].Entity_Type == 'I') {
                if (stateEntities.Entity[i].nodeType == EntityNodeType.Third_Party) {
                    delete stateEntities.Entity[i].Entity_Name;
                } else {
                    delete stateEntities.Entity[i].Org_Entity_Name;
                }
            }
        }

        this.lineData = []; //Empty for every process.

        for (var i in stateEntities.Line) {
            this.lineData.push({ Line_Code: stateEntities.Line[i].Line_Code, Line_ID: stateEntities.Line[i].Line_ID });
            //Remove temp Line_ID added to Contract_Party container node
            if (stateEntities.Line[i].Contract_Party) {
                delete stateEntities.Line[i].Contract_Party.Line_ID;
                delete stateEntities.Line[i].Contract_Party.Line_Code;
            }
            //Remove temp Line_ID added to Contract_Detail container node
            if (stateEntities.Line[i].Contract_Detail) {
                delete stateEntities.Line[i].Contract_Detail.Line_ID;
            }
        }

        for (var i in stateEntities.Party) {
            if (stateEntities.Party[i].Comm_Amount == '' && stateEntities.Party[i].Flat_Amount_Ind == 'N') {
                delete stateEntities.Party[i].Comm_Amount;
            }
            if (stateEntities.Party[i].Agreement && stateEntities.Party[i].Agreement.Agmt_ID == '') {
                delete stateEntities.Party[i].Agreement;
            }
            if (stateEntities.Party[i].Affiliate && stateEntities.Party[i].Affiliate.Entity_ID == '') {
                delete stateEntities.Party[i].Affiliate;
            }
            if (stateEntities.Party[i].nodeType == 'Contract_Broker') {
                //Retail Broker can only be provided if Book Option set to use Retail / Wholesale Brokers.
                if (this.app.getBookOptionValue('Wholesale_Retail_Broker_Ind') != 'Y') {
                    delete stateEntities.Party[i].Retail_Broker;
                }
            }
        }

        for (var i in stateEntities.Phone) {
            if (i.slice(0, 1) == '-') {
                delete stateEntities.Phone[i].Phone_ID;
            }
        }

        for (var i in stateEntities.Article) {
            if (stateEntities.Article[i].Edit_Status == 'X') {
                stateEntities.Article[i].Edit_Status = 'N';
            }
        }

        for (var i in stateEntities.Location) {
            if (stateEntities.Location[i].Edit_Status == 'X') {
                stateEntities.Location[i].Edit_Status = 'N';
            }
        }

        for (var i in stateEntities.SubLocation) {
            if (stateEntities.SubLocation[i].Edit_Status == 'X') {
                stateEntities.SubLocation[i].Edit_Status = 'N';
            }
        }

        for (var i in stateEntities.Name) {
            if (stateEntities.Name[i].Edit_Status == 'X') {
                stateEntities.Name[i].Edit_Status = 'N';
            }
        }

        for (var i in stateEntities.Interest) {
            if (stateEntities.Interest[i].Edit_Status == 'X') {
                stateEntities.Interest[i].Edit_Status = 'N';
            }
        }

        for (var i in stateEntities.Attachment) {
            if (stateEntities.Attachment[i].Edit_Status == 'X') {
                stateEntities.Attachment[i].Edit_Status = 'N';
            }
        }

        for (var i in stateEntities.Form) {
            if (stateEntities.Form[i].Edit_Status == 'X') {
                stateEntities.Form[i].Edit_Status = 'N';
            }
        }

        for (var i in stateEntities.Profile) {
            if (stateEntities.Profile[i].Edit_Status == 'X') {
                stateEntities.Profile[i].Edit_Status = 'N';
            }
        }

        for (var i in stateEntities.Factor) {
            if (stateEntities.Factor[i].Edit_Status == 'X') {
                stateEntities.Factor[i].Edit_Status = 'N';
            }
        }
        for (var i in stateEntities.Product) {
            if (stateEntities.Product[i].Edit_Status == 'X') {
                stateEntities.Product[i].Edit_Status = 'N';
            }

            //Remove product state record and pointer in Article state for Product/cost records with Edit_Status=?
            if (stateEntities.Product[i].Edit_Status == '?') {
                let articleID = stateEntities.Product[i].Article_ID;
                if (articleID) {
                    if (stateEntities.Article[articleID]) {
                        if (stateEntities.Article[articleID].Product) {
                            for (var p = 0; i < stateEntities.Article[articleID].Product.length; p++) {
                                if (stateEntities.Article[articleID].Product[p] === i) {
                                    stateEntities.Article[articleID].Product.splice(p, 1);
                                    break;
                                }
                            }
                        }
                    }
                }

                delete stateEntities.Product[i];
            }
        }

        // TODO: This code can be removed once SDK no longer fails
        //        when providing Adhoc_Ind != Y and Condition_Text.
        for (var i in stateEntities.Condition) {
            if (stateEntities.Condition[i].Adhoc_Ind != 'Y') {
                delete stateEntities.Condition[i].Condition_Text;
            }
        }

        if (
            [TransactionNodeType.Cancel, TransactionNodeType.ExtendTerm, TransactionNodeType.ChangeConsumer].includes(
                this.transactionType
            )
        ) {
            for (var i in stateEntities.Contract) {
                delete stateEntities.Contract[i].Edit_Status;
            }
            for (var i in stateEntities.Line) {
                stateEntities.Line[i].Edit_Status = 'N';
            }
            for (var i in stateEntities.Article) {
                stateEntities.Article[i].Edit_Status = 'N';
            }
        }

        // HACK TO GET AROUND SDK ISSUES WITH NEW UD RECORDS HAVING PROBLEMS WITH NULL OR EMPTY IDs OR GUIDs
        // remove negative ID's from UD_Records.   Also remove UD_Data id and guid columns on record adds
        for (var i in stateEntities.UDTable) {
            let tableConfig: UserDefinedTableConfig = this.store.selectSnapshot(
                UserDefinedObjectState.userDefinedTableConfigByTableName
            )(i);

            if (tableConfig) {
                let idColumn = tableConfig.dbIDColumn.toLowerCase();
                let guidColumn = tableConfig.dbGUIDColumn.toLowerCase();

                if (stateEntities.UDTable[i].UD_Record) {
                    let x = stateEntities.UDTable[i];
                    stateEntities.UDTable[i].UD_Record.forEach((element) => {
                        if (element.UD_Record_ID && element.UD_Record_ID.slice(0, 1) == '-') {
                            delete element.UD_Record_ID;
                            delete element.UD_Record_GUID;
                            delete element.UD_Data[idColumn];
                            delete element.UD_Data[guidColumn];
                        }
                    });
                }
            }

            if (stateEntities.UDTable[i].Parent_Object_Code == UserDefinedParentType.Contract) {
                for (var recordIdx in stateEntities.UDTable[i].UD_Record) {
                    let record = stateEntities.UDTable[i].UD_Record[recordIdx];
                    if (['A', 'C'].includes(record.Edit_Status)) {
                        let locID = record.UD_Data?.loc_id;
                        if (locID) {
                            const location = this.store.selectSnapshot(LocationState.locationByLocID)(locID);
                            if (location && location.Loc_Num) {
                                record.UD_Data.loc_num = location.Loc_Num;
                            }
                        } else {
                            if (locID === '') {
                                record.UD_Data.loc_num = '';
                            }
                        }

                        let subLocID = record.UD_Data?.sub_loc_id;
                        if (subLocID) {
                            const subLocation = this.store.selectSnapshot(SubLocationState.subLocationBySubLocID)(subLocID);
                            if (subLocation && subLocation.Sub_Loc_Num) {
                                record.UD_Data.sub_loc_num = subLocation.Sub_Loc_Num;
                            }
                        } else {
                            if (subLocID === '') {
                                record.UD_Data.sub_loc_num = '';
                            }
                        }
                    }
                }
            }
        }
        // END HACK TO GET AROUND SDK ISSUES WITH NEW RECORDS HAVING PROBLEMS WITH NULL OR EMPTY IDs OR GUIDs

        // build linkage to referenced UD tables in Consumer and Contract nodes
        // MOVE TO A UD State Helper Service?
        let contractUDTableLinks = [];
        let consumerUDTableLinks = [];
        for (var i in stateEntities.UDTable) {
            switch (stateEntities.UDTable[i].Parent_Object_Code) {
                case UserDefinedParentType.Entity:
                    consumerUDTableLinks.push(stateEntities.UDTable[i].Table_Name);
                    break;
                case UserDefinedParentType.Contract:
                    contractUDTableLinks.push(stateEntities.UDTable[i].Table_Name);
                    break;
            }
        }

        for (var i in stateEntities.Contract) {
            stateEntities.Contract[i].UD_Table = contractUDTableLinks;
        }

        for (var i in stateEntities.Entity) {
            if (stateEntities.Entity[i].nodeType == EntityNodeType.Consumer) {
                stateEntities.Entity[i].UD_Table = consumerUDTableLinks;
            }
        }

        //END UD TABLE LINKAGE CODE

        if (
            this.app.getBookOptionValue('Use_ValidationProcedures_Ind') === 'Y' &&
            [EventType.Submit, EventType.Accept, EventType.Reject].includes(event) &&
            this.transactionType != TransactionNodeType.ChangeConsumer
        ) {
            stateEntities.Transaction[1].Event += ';Validate';
        }

        let tran = denormalize('1', rootSchema, stateEntities);
        this.reprocessTran = tran;

        this.dsProcess.process({ json: 'CT2' }, tran);
        this.dsProcessStatusSub = this.dsProcess.status$.subscribe((status) => this.dsProcessStatusUpdated(status));
    }

    dsProcessStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                if (this.dsProcessStatusSub) {
                    this.dsProcessStatusSub.unsubscribe();
                }
                this.pageOS.setPristine();
                //If originally loaded from a Template, the ID will be '-1'.

                if (!this.objectID || this.objectID == '0' || this.objectID == '-1') {
                    if (this.transactionType == TransactionNodeType.Contract) {
                        this.objectID = this.dsProcess.objectNode.Contract_ID;
                        this.contractID = this.objectID;
                        this.app.pageParams.setParameter(this.pageID, 'Contract_ID', this.contractID); // add new objectID to page parameters
                    } else {
                        this.objectID = this.dsProcess.objectNode.Proc_ID;
                        //TODO:  HACK - contract procs do not return the proc_id on newly created procs  -- parse id out of process message
                        let temp: string = this.dsProcess.objectNode.Process_Message;
                        temp = temp.split('Proc_ID=')[1];
                        if (temp) {
                            this.objectID = temp.split(')')[0];
                            this.app.pageParams.setParameter(this.pageID, 'Proc_ID', this.objectID); // add new objectID to page parameters
                        }

                        //TODO: Hack to Get Contract_ID on newly created contracts -- parse id out of process message if not in Contract_ID object.
                        if (!this.contractID) {
                            this.contractID = this.dsProcess.objectNode.Contract.Contract_ID;
                            if (!this.contractID || this.contractID == '') {
                                temp = this.dsProcess.objectNode.Contract.Process_Message;
                                if (temp) {
                                    temp = temp.split('Contract_ID=')[1];
                                    this.contractID = temp.split(')')[0];
                                }
                            }
                        }
                    }
                    //TODO: Filter down when this sets the Contract as recent Item. This may be to frequent..?
                    if (this.contractID) {
                        this.app.setRecentItem('Contract', this.contractID);
                    }
                }

                //TODO: Skip reload when coming back from delete. Then goes back to the previous page..?
                let taskID = this.dsProcess.tranNode.Task_ID;
                this.app.pageParams.setParameter(this.pageID, 'Task_ID', taskID); // add new taskID to page parameters
                this.taskOS.loadTask(taskID, true, () => {
                    //loadObject will be called IF the task is NOT resolved (non-terminating event, staying on current page)
                    this.loadObject(this.objectID);
                });
                break;
            case DataSourceStatus.Error:
                if (this.dsProcessStatusSub) {
                    this.dsProcessStatusSub.unsubscribe();
                }
                this.launchRating = false;
                if (
                    this.dsProcess.replyNode.Reply_Code == '-5004' &&
                    this.dsProcess.tranNode &&
                    this.dsProcess.tranNode.Validations &&
                    this.dsProcess.tranNode.Validations.Validation
                ) {
                    this.dsProcess.setStatus(DataSourceStatus.Initialized); // Reset status.
                    let validations: UserDefinedValidation[] = [];
                    for (var v in this.dsProcess.tranNode.Validations.Validation) {
                        validations.push(this.dsProcess.tranNode.Validations.Validation[v]);
                    }
                    if (validations.length > 0) {
                        this.pageOS.setUserDefinedValidations(validations, this.continueAfterWarnings.bind(this));
                    }
                }
        }
    }

    continueAfterWarnings(result: any) {
        if (result === true) {
            if (this.reprocessTran) {
                let tranString = JSON.stringify(this.reprocessTran);
                tranString = tranString.replace(';Validate', '');
                this.reprocessTran = JSON.parse(tranString);

                this.dsProcess.process({ json: 'CT2' }, this.reprocessTran);
                this.dsProcessStatusSub = this.dsProcess.status$.subscribe((x) => this.dsProcessStatusUpdated(x));
            }
        }
    }

    dsGetStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                if (this.dsGetStatusSub) {
                    this.dsGetStatusSub.unsubscribe();
                }
                this.loadData(this.dsGet.data, false);
                break;
            case DataSourceStatus.Error:
                if (this.dsGetStatusSub) {
                    this.dsGetStatusSub.unsubscribe();
                }
                break;
        }
    }

    setValidEvents() {
        // !!!!! Set valid events in Page State !!!!
        let events: PageEventType[] = [];
        let saveValidationEvent: PageEventType = PageEventType.Create;
        let anticipatedValidationEvent: PageEventType = PageEventType.Submit;

        if (this.pageOS.isLocked()) {
            //Only set valid events if a lock exists.
            if (this.transactionType != TransactionNodeType.Contract && this.dsGet.objectNode) {
                switch (this.dsGet.objectNode.Proc_Status) {
                    case '':
                    case 'I':
                        saveValidationEvent = PageEventType.Create;
                        anticipatedValidationEvent = PageEventType.Submit;
                        events.push(PageEventType.Update);
                        events.push(PageEventType.Delete);
                        events.push(PageEventType.Submit);
                        events.push(PageEventType.Accept);
                        //Reject (Unschedule) only displays once Submitted (Scheduled). 
                        // For all other Procedures, Reject displays for non submitted objects.
                        if (this.transactionType != TransactionNodeType.ScheduleCancel) {
                            events.push(PageEventType.Reject);
                        }
                        break;
                    case 'S':
                        saveValidationEvent = PageEventType.Submit;
                        anticipatedValidationEvent = PageEventType.Accept;
                        events.push(PageEventType.Update);
                        events.push(PageEventType.Accept);
                        events.push(PageEventType.Reject);
                        break;
                }
            } else {
                // Update is only valid Event for ContractProcess
                saveValidationEvent = PageEventType.Create;
                anticipatedValidationEvent = PageEventType.Create;
                events.push(PageEventType.Update);
            }
        }

        this.pageOS.setValidEvents(events, saveValidationEvent, anticipatedValidationEvent);
    }

    newObject(tranType?: TransactionNodeType) {
        this.objectID = null;
        this.store.dispatch(new InitializeTransaction(tranType));
        switch (tranType) {
            case TransactionNodeType.Application:
            case TransactionNodeType.Market:
            case TransactionNodeType.Quote:
            case TransactionNodeType.Bind:
            case TransactionNodeType.Issue:
            case TransactionNodeType.Endorsement:
            case TransactionNodeType.ScheduleCancel:
            case TransactionNodeType.Cancel:
            case TransactionNodeType.Reinstate:
            case TransactionNodeType.SelfAudit:
            case TransactionNodeType.Audit:
            case TransactionNodeType.ExtendTerm:
            case TransactionNodeType.Renewal:
            case TransactionNodeType.Reissue:
            case TransactionNodeType.Rewrite:
            case TransactionNodeType.ContractDisable:
            case TransactionNodeType.ChangeConsumer:
            case TransactionNodeType.NonRenew:
            case TransactionNodeType.ContractRate:
                break;
            default:
                throw "'" + tranType.toString() + "' is not currently supported by newObject!";
        }
    }

    addConsumer(consumer: any) {
        this.store.dispatch(new AddConsumer(consumer));
    }

    updateContract(contract: any) {
        this.store.dispatch(new UpdateContract(contract));
    }

    updateContractProcedure(procedure: any) {
        let currentProcedure = this.store.selectSnapshot(ContractProcedureState.procedure);

        this.store.dispatch(new UpdateContractProcedure(procedure)).subscribe((action) => {
            if (this.transactionType == TransactionNodeType.Cancel) {
                //set auto calc net change to y and auto save based on changes to min earned
                if (
                    procedure.Min_Earned_Ind != currentProcedure.Min_Earned_Ind ||
                    procedure.Min_Earned_Amount != currentProcedure.Min_Earned_Amount ||
                    procedure.Min_Earned_Factor != currentProcedure.Min_Earned_Factor
                ) {
                    this.process(EventType.Update, {
                        Auto_Calculate_Net_Change: 'Y'
                    });
                }
            }
        });
    }

    cloneArticle(articleID: number, copies: number = 1, copyProducts: boolean = true) {
        this.store.dispatch(new CloneArticle(articleID, copies, copyProducts));
    }

    addLine(line: any) {
        this.store.dispatch(new AddLine(line));
    }

    deleteLine(lineID: number) {
        this.store.dispatch(new DeleteLine(lineID));
    }

    updateLine(line: any) {
        this.store.dispatch(new UpdateLine(line));
    }

    setCurrentArticle(article: any) {
        this.store.dispatch(new SetCurrentArticle(article));
    }

    updateArticle(article: any) {
        this.store.dispatch(new UpdateArticle(article));
    }

    addArticle(lineID: string, articleType: ArticleNodeType) {
        this.store.dispatch(new AddArticle(lineID, articleType)).subscribe(() => {
            this.autoDisplayProductSets();
        });
    }

    addArticleWithProfile(lineID: string, articleType: ArticleNodeType, article: any = {}, profile: any = {}) {
        this.store.dispatch(new AddArticle(lineID, articleType, article, profile)).subscribe(() => {
            this.autoDisplayProductSets();
        });
    }

    deleteArticle(articleID: number) {
        this.store.dispatch(new DeleteArticle(articleID));
    }

    restoreArticle(articleID: number) {
        this.store.dispatch(new RestoreArticle(articleID));
    }

    addProductsToArticle(articleID: number, products: any[], autoDisplay: boolean = false) {
        this.store.dispatch(new AddProductsToArticle(articleID, products, autoDisplay));
    }

    deleteProduct(costID: any) {
        this.store.dispatch(new DeleteProduct(costID));
    }

    restoreProduct(costID: any) {
        this.store.dispatch(new RestoreProduct(costID));
    }

    selectAvailableProduct(costID: any) {
        this.store.dispatch(new SelectAvailableProduct(costID));
    }

    removedSharedProduct(costID: any) {
        this.store.dispatch(new RemoveSharedProduct(costID));
        this.pageOS.setDirty();
    }

    addSubArticle(articleID: number, subArticleNodeType: SubArticleNodeType, subArticle?: any) {
        this.store.dispatch(new AddSubArticle(articleID, subArticleNodeType, subArticle));
    }

    addVehicleDriverSubArticle(vehNum: any, subArticle: any) {
        this.store.dispatch(new AddVehicleDriverSubArticle(vehNum, subArticle));
    }

    updateVehicleDriverSubArticle(subArticle: any) {
        this.store.dispatch(new UpdateVehicleDriverSubArticle(subArticle));
    }

    updateSubArticle(articleID: number, subArticle: any) {
        this.store.dispatch(new UpdateSubArticle(articleID, subArticle));
    }

    deleteSubArticle(articleID: number, subArticleID: string) {
        this.store.dispatch(new DeleteSubArticle(articleID, subArticleID));
    }

    restoreSubArticle(articleID: number, subArticleID: string) {
        this.store.dispatch(new RestoreSubArticle(articleID, subArticleID));
    }

    cloneSubArticle(articleID: number, subArticleID: string, copies: number = 1) {
        this.store.dispatch(new CloneSubArticle(articleID, subArticleID, copies));
    }

    // ContractForm

    cloneForm(articleID: number, copies: number = 1) {
        this.store.dispatch(new CloneForm(articleID, copies));
    }

    updateForm(form: any) {
        this.store.dispatch(new UpdateForm(form));
    }

    addForm(lineID: string, form: any) {
        this.store.dispatch(new AddForm(lineID, form));
    }

    deleteForm(articleID: number) {
        this.store.dispatch(new DeleteForm(articleID));
    }

    restoreForm(articleID: number) {
        this.store.dispatch(new RestoreForm(articleID));
    }

    // Contract Factor

    cloneFactor(factorID: number, copies: number = 1) {
        this.store.dispatch(new CloneFactor(factorID, copies));
    }

    updateFactor(form: any) {
        this.store.dispatch(new UpdateFactor(form));
    }

    addFactor(lineID, string, form: any = {}) {
        this.store.dispatch(new AddFactor(lineID, form));
    }

    deleteFactor(factorID: number) {
        this.store.dispatch(new DeleteFactor(factorID));
    }

    restoreFactor(factorID: number) {
        this.store.dispatch(new RestoreFactor(factorID));
    }

    //Contract Location
    setCurrentLocation(location: any) {
        this.store.dispatch(new SetCurrentLocation(location));
    }

    addLocation(locationTemplate: any = {}) {
        this.store.dispatch(new AddLocation(locationTemplate));
    }

    updateLocation(location: any) {
        this.store.dispatch(new UpdateLocation(location));
    }

    cloneLocation(locID: number, copies: number = 1, locNum: string = null) {
        this.store.dispatch(new CloneLocation(locID, copies, locNum));
    }

    deleteLocation(locID: number) {
        this.store.dispatch(new OnLocationDeleted(locID));
    }

    restoreLocation(locID: number) {
        this.store.dispatch(new RestoreLocation(locID));
    }

    setCurrentParentLocation(locNum: string) {
        this.store.dispatch(new SetCurrentSubLocationParent(locNum));
    }

    setCurrentSubLocation(subLocation: any) {
        this.store.dispatch(new SetCurrentSubLocation(subLocation));
    }

    addSubLocation(subLocTemplate: any = {}, locID: number = null) {
        this.store.dispatch(new AddSubLocation(subLocTemplate, locID));
    }

    updateSubLocation(subLocation: any, locID: number = null) {
        this.store.dispatch(new UpdateSubLocation(subLocation, locID));
    }

    deleteSubLocation(locID: number, subLocID: number) {
        this.store.dispatch(new DeleteSubLocation(locID, subLocID));
    }

    cloneSubLocation(locID: number, subLocID: number, copies: number = 1) {
        this.store.dispatch(new CloneSubLocation(locID, subLocID, copies));
    }

    restoreSubLocation(locID: number, subLocID: number) {
        this.store.dispatch(new RestoreSubLocation(locID, subLocID));
    }

    //Contract Prior History

    addPriorHistory(priorHistoryTemplate: any = {}) {
        this.store.dispatch(new AddPriorHistory(priorHistoryTemplate));
    }

    updatePriorHistory(priorHistory: any) {
        this.store.dispatch(new UpdatePriorHistory(priorHistory));
    }

    deletePriorHistory(priorHistoryID: string) {
        this.store.dispatch(new DeletePriorHistory(priorHistoryID));
    }

    restorePriorHistory(priorHistoryID: string) {
        this.store.dispatch(new RestorePriorHistory(priorHistoryID));
    }

    //Contract Loss History

    addLossHistory(lossHistoryTemplate: any = {}, priorHistoryID: string) {
        this.store.dispatch(new AddLossHistory(lossHistoryTemplate, priorHistoryID));
    }

    updateLossHistory(lossHistory: any) {
        this.store.dispatch(new UpdateLossHistory(lossHistory));
    }

    deleteLossHistory(lossHistoryID: string) {
        this.store.dispatch(new DeleteLossHistory(lossHistoryID));
    }

    restoreLossHistory(lossHistoryID: string) {
        this.store.dispatch(new RestoreLossHistory(lossHistoryID));
    }

    //Contract Name

    addName(nameTemplate: any = {}) {
        this.store.dispatch(new AddName(nameTemplate));
    }

    updateName(name: any) {
        this.store.dispatch(new UpdateName(name));
    }

    cloneName(nameID: number, copies: number = 1) {
        this.store.dispatch(new CloneName(nameID, copies));
    }

    deleteName(nameID: number) {
        this.store.dispatch(new DeleteName(nameID));
    }

    restoreName(nameID: number) {
        this.store.dispatch(new RestoreName(nameID));
    }

    //Team Member

    addTeamMember(teamMemberTemplate: any = {}) {
        this.store.dispatch(new AddTeamMember(teamMemberTemplate));
    }

    updateTeamMember(teamMember: any) {
        this.store.dispatch(new UpdateTeamMember(teamMember));
    }

    cloneTeamMember(partyID: number, copies: number = 1) {
        this.store.dispatch(new CloneTeamMember(partyID, copies));
    }

    deleteTeamMember(partyID: number) {
        this.store.dispatch(new DeleteTeamMember(partyID));
    }

    restoreTeamMember(partyID: number) {
        this.store.dispatch(new RestoreTeamMember(partyID));
    }

    //Contract Attachment

    addAttachment(attachment: any = {}) {
        this.store.dispatch(new AddAttachment(attachment));
    }

    updateAttachment(attachment: any) {
        this.store.dispatch(new UpdateAttachment(attachment));
    }

    cloneAttachment(attachmentID: number, copies: number = 1) {
        this.store.dispatch(new CloneAttachment(attachmentID, copies));
    }

    deleteAttachment(attachmentID: number) {
        this.store.dispatch(new DeleteAttachment(attachmentID));
    }

    restoreAttachment(attachmentID: number) {
        this.store.dispatch(new RestoreAttachment(attachmentID));
    }

    updateAttachmentDocumentData(attachmentID: number, documentData: any) {
        this.store.dispatch(new UpdateAttachmentDocumentData(attachmentID, documentData));
    }

    //Contract Condition

    addCondition(conditionTemplate: any = {}) {
        this.store.dispatch(new AddCondition(conditionTemplate));
    }

    updateCondition(condition: any) {
        this.store.dispatch(new UpdateCondition(condition));
    }

    // cloneCondition(conditionID: number, copies: number = 1) {
    //   this.store.dispatch(new CloneCondition(conditionID, copies));
    // }

    deleteCondition(conditionID: number) {
        this.store.dispatch(new DeleteCondition(conditionID));
    }

    restoreCondition(conditionID: number) {
        this.store.dispatch(new RestoreCondition(conditionID));
    }

    //Contract Interest

    addInterest(thirdParty: any, interestTemplate: any = {}, reference: any = { Ref_Type: 'Contract' }) {
        this.store.dispatch(new AddInterest(thirdParty, interestTemplate, reference));
    }

    updateInterest(interest: any) {
        this.store.dispatch(new UpdateInterest(interest));
    }

    cloneInterest(interestID: number, copies: number = 1) {
        this.store.dispatch(new CloneInterest(interestID, copies));
    }

    deleteInterest(interestID: number) {
        this.store.dispatch(new DeleteInterest(interestID));
    }

    restoreInterest(interestID: number) {
        this.store.dispatch(new RestoreInterest(interestID));
    }

    //Contract Profile

    addProfile(profileTemplate: any = {}, articleID: number = null) {
        this.store.dispatch(new AddProfile(profileTemplate, articleID));
    }

    updateProfile(profile: any) {
        this.store.dispatch(new UpdateProfile(profile));
    }

    cloneProfile(profileID: number, copies: number = 1) {
        this.store.dispatch(new CloneProfile(profileID, copies));
    }

    deleteProfile(profileID: number) {
        this.store.dispatch(new DeleteProfile(profileID));
    }

    restoreProfile(profileID: number) {
        this.store.dispatch(new RestoreProfile(profileID));
    }

    linkProfileToArticle(profileID: number, articleID: number) {
        this.store.dispatch(new LinkProfileToArticle(profileID, articleID));
    }

    //Used for Broker and Supplier Party. Ok to name generically?

    updateParty(party: any) {
        this.store.dispatch(new UpdateParty(party));
    }

    updatePartyAgreement(party: any, agmtData: any = {}) {
        this.store.dispatch(new UpdatePartyAgreement(party, agmtData));
    }
    clearParty(partyID: number, sync: boolean = false) {
        this.store.dispatch(new ClearParty(partyID, sync));
    }

    //Broker Party
    addBrokerParty(brokerPartyTemplate: any = {}) {
        this.store.dispatch(new AddParty(PartyNodeType.Contract_Broker, brokerPartyTemplate));
    }

    updateBrokerParty(brokerParty: any) {
        this.store.dispatch(new UpdateParty(brokerParty));
    }

    cloneBrokerParty(partyID: number, copies: number = 1) {
        this.store.dispatch(new CloneParty(partyID, copies));
    }

    deleteBrokerParty(partyID: number) {
        this.store.dispatch(new DeleteParty(partyID));
    }

    restoreBrokerParty(partyID: number) {
        this.store.dispatch(new RestoreParty(partyID));
    }

    //Supplier Party

    addSupplierParty(supplierPartyTemplate: any = {}) {
        this.store.dispatch(new AddParty(PartyNodeType.Contract_Supplier, supplierPartyTemplate));
    }

    updateSupplierParty(brokerParty: any) {
        this.store.dispatch(new UpdateParty(brokerParty));
    }

    cloneSupplierParty(partyID: number, copies: number = 1) {
        this.store.dispatch(new CloneParty(partyID, copies));
    }

    deleteSupplierParty(partyID: number) {
        this.store.dispatch(new DeleteParty(partyID));
    }

    restoreSupplierParty(partyID: number) {
        this.store.dispatch(new RestoreParty(partyID));
    }

    //Producer Party

    addProducerParty(producerPartyTemplate: any = {}) {
        this.store.dispatch(new AddParty(PartyNodeType.Contract_Producer, producerPartyTemplate));
    }

    updateProducerParty(brokerParty: any) {
        this.store.dispatch(new UpdateParty(brokerParty));
    }

    cloneProducerParty(partyID: number, copies: number = 1) {
        this.store.dispatch(new CloneParty(partyID, copies));
    }

    deleteProducerParty(partyID: number) {
        this.store.dispatch(new DeleteParty(partyID));
    }

    restoreProducerParty(partyID: number) {
        this.store.dispatch(new RestoreParty(partyID));
    }

    //FinanceCo Party

    addFinanceCoParty(financeCoPartyTemplate: any = {}) {
        this.store.dispatch(new AddParty(PartyNodeType.Contract_FinanceCo, financeCoPartyTemplate));
    }

    updateFinanceCoParty(brokerParty: any) {
        this.store.dispatch(new UpdateParty(brokerParty));
    }

    cloneFinanceCoParty(partyID: number, copies: number = 1) {
        this.store.dispatch(new CloneParty(partyID, copies));
    }

    deleteFinanceCoParty(partyID: number) {
        this.store.dispatch(new DeleteParty(partyID));
    }

    restoreFinanceCoParty(partyID: number) {
        this.store.dispatch(new RestoreParty(partyID));
    }

    // Product

    updateProduct(product: any, forceCalc: boolean = false) {
        //Delete extra data if provided - Added by productsByArticleAndCostType state selector.
        delete product.Article_Status;
        delete product.Article_Desc;
        delete product.Article_Type;
        delete product.Article_Class_Code;
        delete product.Article_Territory_Code;
        delete product.Loc_Num;
        delete product.State;
        delete product.State_Desc;
        delete product.Sub_Loc_Num;
        delete product.Line_Code;
        delete product.Line_Code_Desc;
        delete product.Fully_Earned_Ind_Desc;
        delete product.Limit_Deduct;

        let autoCalcTaxInd: string = 'N';
        let autoCalcFeeInd: string = 'N';
        let error: boolean = false;

        if (this.transactionType == TransactionNodeType.Cancel) {
            let messageString: any;

            let currentProduct = this.store.selectSnapshot(ProductState.productsByIDs)([product.Cost_ID]);
            let currWritten = parseFloat(currentProduct[0]?.Written_Amount);
            let currNetChange = parseFloat(currentProduct[0]?.Net_Change_Amount);
            let updateNetChange = parseFloat(product.Net_Change_Amount);
            if (currWritten - currNetChange < 0) {
                if (currWritten + updateNetChange - currNetChange > 0) {
                    error = true;
                    messageString = 'Written amount cannot be positive';
                }
            } else if (currWritten + updateNetChange - currNetChange < 0) {
                error = true;
                messageString = 'Written amount must be greater than or equal to fully earned premium';
            }

            if (error) {
                //set the amount back to its original value
                product.Net_Change_Amount = currentProduct[0].Net_Change_Amount;

                this.app.showAlert({
                    title: 'Warning',
                    message: messageString
                });
            }

            //auto calculate taxes and fees if book option for tax or fee is set to use tran rules
            //auto save in case user then attempts to set min earned amount/pct
            if (!error) {
                if (currNetChange != updateNetChange) {
                    let bookFeeOption = this.app.getBookOptionValue('Calc_Fee_At_Cancel_Method');
                    let bookTaxOption = this.app.getBookOptionValue('Calc_Tax_At_Cancel_Method');

                    bookFeeOption == 'R' ? (autoCalcFeeInd = 'Y') : (autoCalcFeeInd = 'N');
                    bookTaxOption == 'R' ? (autoCalcTaxInd = 'Y') : (autoCalcTaxInd = 'N');
                }
            }
        }

        this.store.dispatch(new UpdateProduct(product, forceCalc)).subscribe((action) => {
            if (
                !error &&
                (product?.Product_Cost_Type == 'P' || product?.Product_Cost_Type == 'F') &&
                this.transactionType == TransactionNodeType.Cancel
            ) {
                this.process(EventType.Update, {
                    Auto_Calculate_Taxes: autoCalcTaxInd,
                    Auto_Calculate_Fees: autoCalcFeeInd
                });
            }
        });
    }

    checkForPrimaryPerilProduct() {
        let lineCode: any;
        let products: any;
        let ppProducts: any;
        let idx = -1;
        let articles = this.store.selectSnapshot(ArticleState.articlesByType)([
            ArticleNodeType.CDPC_Blanket_Rating,
            ArticleNodeType.CDPC_Class_Rating
        ]);

        for (var a in articles) {
            if (articles[a].Product && articles[a].Product.length > 0) {
                lineCode = this.store.selectSnapshot(LineState.lineByLineID)(articles[a].Line_ID).Line_Code;
                products = this.store.selectSnapshot(ProductState.productsByIDs)(articles[a].Product);
                ppProducts = this.store.selectSnapshot(ProductLineConfigObjectState.productsByExclusivityGroup)(lineCode, 'A');

                for (var p in products) {
                    idx = ppProducts.map((ppp) => ppp.Product_Code).indexOf(products[p].Product_Code);
                    if (idx > -1) {
                        this.setPrimaryPerilProduct(articles[a].Article_ID, products[p].Product_Code);
                        break;
                    }
                }
            }
        }
    }

    setPrimaryPerilProduct(articleID: number, productCode: string) {
        this.store.dispatch(new SetPrimaryPerilProduct(articleID, productCode));
    }

    addThirdPartyEntity(entity: any, interestID: string) {
        this.store.dispatch(new AddThirdPartyEntity(entity, interestID));
    }

    updateConsumer(consumer: any) {
        this.store.dispatch(new UpdateEntity(consumer));
    }
    updateEntity(entity: any) {
        this.store.dispatch(new UpdateEntity(entity));
    }

    deleteThirdPartyEntity(entityID: string) {
        this.store.dispatch(new DeleteThirdPartyEntity(entityID));
    }

    addAddress(entityID: string = null, addressTemplate: any = {}) {
        this.store.dispatch(new AddAddress(entityID, addressTemplate));
    }

    updateAddress(entityID: string = null, address: any) {
        this.store.dispatch(new UpdateAddress(entityID, address));
    }

    cloneAddress(entityID: string = null, addressID: number) {
        this.store.dispatch(new CloneAddress(entityID, addressID));
    }

    deleteAddress(entityID: string = null, addressID: number) {
        this.store.dispatch(new DeleteAddress(entityID, addressID));
    }

    restoreAddress(addressID: number) {
        this.store.dispatch(new RestoreAddress(addressID));
    }

    addPhone(entityID: string = null, phoneTemplate: any = {}) {
        this.store.dispatch(new AddPhone(entityID, phoneTemplate));
    }

    updatePhone(entityID: string, phone: any) {
        this.store.dispatch(new UpdatePhone(entityID, phone));
    }

    clonePhone(entityID: string = null, phoneID: number) {
        this.store.dispatch(new ClonePhone(entityID, phoneID));
    }

    deletePhone(entityID: string = null, phoneID: number) {
        this.store.dispatch(new DeletePhone(entityID, phoneID));
    }

    restorePhone(phoneID: number) {
        this.store.dispatch(new RestorePhone(phoneID));
    }

    //TODO: Refactor - standardize methodology for use in other popup edits????  Especially the calls to modalService/modalRef
    modalLocationEdit(location?: any) {
        if (location && (location.Loc_ID || location.Loc_Num)) {
            this.setCurrentLocation(location);
        } else {
            // add a location if edit clicked with no location selected
            this.addLocation();
        }

        if (this.locationSub) {
            this.locationSub.unsubscribe(); //IS THIS NEEDED?
        }

        //when location is added it will become the current location, if deleted the current loc is cleared (trigger the popup to close)
        this.locationSub = this.location$.subscribe((x) => {
            if (!x.Loc_ID) {
                this.closeModalLocationEdit();
            }
        });

        /////////////////////////////////////////////////////////////////////////////////////
        // TODO:  Put this block of code into a service.   App Service?  Something like openDynamicModalComponent(type, data)
        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'lg',
            injector: this.injector,
            backdrop: 'static',
            windowClass: 'ep-modal-edit-panel'
        });
        this.modalRef.componentInstance.type = 'LocationEditPanelComponent';
        this.modalRef.componentInstance.showOKButton = true;
        this.modalRef.componentInstance.dynamicData = { modal: true };
        this.modalRef.shown.pipe(take(1)).subscribe(() => this.modalFocusService.focusFirstField());
        /////////////////////////////////////////////////////////////////////////////////////
    }

    private closeModalLocationEdit() {
        if (this.modalRef) {
            this.modalRef.close();
        }
        if (this.locationSub) {
            this.locationSub.unsubscribe();
        }
    }

    modalSubLocationEdit(subLocation?: any) {
        if (subLocation && subLocation.Loc_ID && subLocation.Sub_Loc_Num) {
            this.setCurrentSubLocation(subLocation);
        } else {
            // add a sub location if edit clicked with no sub location selected
            this.addSubLocation(subLocation);
        }

        if (this.subLocationSub) {
            this.subLocationSub.unsubscribe();
        }

        //when sub location is added it will become the current sub location, if deleted the current sub loc is cleared (trigger the popup to close)
        this.subLocationSub = this.store.select(SubLocationState.currentSubLocation).subscribe((currentLoc) => {
            this.currentSubLoc = currentLoc;
            if (this.currentSubLoc && !this.currentSubLoc.Sub_Loc_ID) {
                this.closeModalSubLocationEdit();
            }
        });

        /////////////////////////////////////////////////////////////////////////////////////
        // TODO:  Put this block of code into a service.   App Service?  Something like openDynamicModalComponent(type, data)
        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'lg',
            injector: this.injector,
            backdrop: 'static',
            windowClass: 'ep-modal-edit-panel'
        });
        this.modalRef.componentInstance.type = 'SubLocationEditPanelComponent';
        this.modalRef.componentInstance.showOKButton = true;
        this.modalRef.componentInstance.dynamicData = { modal: true };
        this.modalRef.shown.pipe(take(1)).subscribe(() => this.modalFocusService.focusFirstField());
        /////////////////////////////////////////////////////////////////////////////////////
    }

    closeModalSubLocationEdit() {
        if (this.modalRef) {
            this.modalRef.close();
        }
        if (this.subLocationSub) {
            this.subLocationSub.unsubscribe();
        }
    }

    modalVehicleEdit(lineID: any, vehicle?: any) {
        //console.log('modalVehicleEdit -> begin');
        //console.log('modalVehicleEdit-vehicle -> ' + JSON.stringify(vehicle));
        if (vehicle && vehicle.Article_ID) {
            //console.log('modalVehicleEdit-Edit Vehicle -> ' + JSON.stringify(vehicle));
            this.setCurrentArticle(vehicle);
        } else {
            // add a vehicle if edit clicked with no vehicle selected
            //console.log('modalVehicleEdit-Add Vehicle -> ');
            this.addArticle(lineID, ArticleNodeType.CDAA_Vehicle);
        }

        if (this.articleSub) {
            this.articleSub.unsubscribe(); //IS THIS NEEDED?
        }

        //when vehicle is added it will become the current article, if deleted the current article is cleared (trigger the popup to close)
        this.articleSub = this.article$.subscribe((x) => {
            if (!x.Article_ID) {
                this.closeModalVehicleEdit();
            }
        });

        /////////////////////////////////////////////////////////////////////////////////////
        // TODO:  Put this block of code into a service.   App Service?  Something like openDynamicModalComponent(type, data)
        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'lg',
            injector: this.injector,
            windowClass: 'ep-modal-edit-panel',
            backdrop: 'static'
        });
        this.modalRef.componentInstance.type = 'CDAAVehicleEditPanelComponent';
        this.modalRef.componentInstance.showOKButton = true;
        this.modalRef.componentInstance.dynamicData = { modal: true };
        this.modalRef.shown.pipe(take(1)).subscribe(() => this.modalFocusService.focusFirstField());
        /////////////////////////////////////////////////////////////////////////////////////
    }

    private closeModalVehicleEdit() {
        if (this.modalRef) {
            this.modalRef.close();
        }
        if (this.articleSub) {
            this.articleSub.unsubscribe();
        }
    }

    rejectDialog() {
        let rejectDialogComponent: string = null;

        switch (this.transactionType) {
            case TransactionNodeType.Application:
                rejectDialogComponent = 'ContractApplicationRejectEditPanelComponent';
                break;
            case TransactionNodeType.Bind:
                rejectDialogComponent = 'ContractBindRejectEditPanelComponent';
                break;
            case TransactionNodeType.Issue:
                rejectDialogComponent = 'ContractIssueRejectEditPanelComponent';
                break;
            case TransactionNodeType.Quote:
                rejectDialogComponent = 'ContractQuoteRejectEditPanelComponent';
                break;
            case TransactionNodeType.Market:
                rejectDialogComponent = 'ContractMarketRejectEditPanelComponent';
                break;
            case TransactionNodeType.Endorsement:
                rejectDialogComponent = 'ContractEndorseRejectEditPanelComponent';
                break;
            case TransactionNodeType.ScheduleCancel:
                rejectDialogComponent = 'ContractScheduleCancelRejectEditPanelComponent';
                break;
            case TransactionNodeType.Cancel:
                rejectDialogComponent = 'ContractCancelRejectEditPanelComponent';
                break;
            case TransactionNodeType.Reinstate:
                rejectDialogComponent = 'ContractReinstateRejectEditPanelComponent';
                break;
            case TransactionNodeType.SelfAudit:
                rejectDialogComponent = 'ContractSelfAuditRejectEditPanelComponent';
                break;
            case TransactionNodeType.Audit:
                rejectDialogComponent = 'ContractAuditRejectEditPanelComponent';
                break;
            case TransactionNodeType.ExtendTerm:
                rejectDialogComponent = 'ContractExtendTermRejectEditPanelComponent';
                break;
            case TransactionNodeType.Renewal:
                rejectDialogComponent = 'ContractRenewRejectEditPanelComponent';
                break;
            case TransactionNodeType.Reissue:
                rejectDialogComponent = 'ContractReissueRejectEditPanelComponent';
                break;
            case TransactionNodeType.Rewrite:
                rejectDialogComponent = 'ContractRewriteRejectEditPanelComponent';
                break;
            case TransactionNodeType.ChangeConsumer:
                rejectDialogComponent = 'ContractChangeConsumerRejectEditPanelComponent';
                break;
            case TransactionNodeType.NonRenew:
                rejectDialogComponent = 'ContractNonRenewRejectEditPanelComponent';
                break;
            case TransactionNodeType.ContractRate:
                rejectDialogComponent = 'ContractRateRejectEditPanelComponent';
                break;
            default:
                throw 'no reject dialog component specified for this transaction type';
        }

        if (rejectDialogComponent) {
            /////////////////////////////////////////////////////////////////////////////////////
            // TODO:  Put this block of code into a service.   App Service?  Something like openDynamicModalComponent(type, data)
            this.modalRef = this.modalService.open(DynamicContentModalComponent, {
                centered: true,
                size: 'lg',
                injector: this.injector,
                windowClass: 'ep-modal-edit-panel',
                backdrop: 'static'
            });
            this.modalRef.componentInstance.type = rejectDialogComponent;
            this.modalRef.componentInstance.dynamicData = { modal: true };
            /////////////////////////////////////////////////////////////////////////////////////
        } else {
            // reject without dialog
            this.process(EventType.Reject);
        }
    }

    closeRejectDialog(rejectConfirmed: boolean) {
        if (this.modalRef) {
            this.modalRef.close();
        }
        if (rejectConfirmed) {
            this.process(EventType.Reject);
        }
    }
    addDefaultLocation() {
        return new Promise(async (resolve) => {
            let readOnlyMode = this.store.selectSnapshot(PageState.readOnlyMode);
            if (
                !this.app.DesignerMode &&
                !readOnlyMode &&
                this.transactionType == TransactionNodeType.Application &&
                this.app.getBookOptionValue('Create_Loc_On_App_Ind') == 'Y'
            ) {
                this.store.dispatch(new AddDefaultLocation()).subscribe((action) => {
                    resolve(true);
                });
            } else {
                resolve(true);
            }
        });
    }

    templateDialog(pageID, callbackFunction: any) {
        let templateDialogComponent: string = null;

        switch (this.transactionType) {
            case TransactionNodeType.Endorsement:
                templateDialogComponent = 'ContractEndorseTemplateEditPanelComponent';
                break;
            case TransactionNodeType.Cancel:
                templateDialogComponent = 'ContractCancelTemplateEditPanelComponent';
                break;
            case TransactionNodeType.SelfAudit:
                templateDialogComponent = 'ContractSelfAuditTemplateEditPanelComponent';
                break;
            case TransactionNodeType.Renewal:
                templateDialogComponent = 'ContractRenewTemplateEditPanelComponent';
                break;
            case TransactionNodeType.ExtendTerm:
                templateDialogComponent = 'ContractExtendTermTemplateEditPanelComponent';
                break;
            default:
                throw 'no template dialog component specified for this transaction type';
        }

        if (templateDialogComponent) {
            /////////////////////////////////////////////////////////////////////////////////////
            // TODO:  Put this block of code into a service.   App Service?  Something like openDynamicModalComponent(type, data)
            this.modalRef = this.modalService.open(DynamicContentModalComponent, {
                centered: true,
                size: 'lg',
                injector: this.injector,
                windowClass: 'ep-modal-edit-panel',
                backdrop: 'static'
            });
            this.modalRef.componentInstance.type = templateDialogComponent;
            this.modalRef.componentInstance.dynamicData = { modal: true, pageID: pageID };

            /////////////////////////////////////////////////////////////////////////////////////

            this.modalRef.result.then(
                (result) => {
                    callbackFunction(result);
                },
                () => {
                    callbackFunction({ action: ContractProcTemplateActionTypes.Cancel });
                }
            );
        }
    }

    closeTemplateDialog(returnValue: any) {
        if (this.modalRef) {
            this.modalRef.close(returnValue);
        }
    }

    validate(event: PageEventType) {
        // merge in the user defined table data with contract state data before validation.
        let stateEntities = {
            ...this.store.selectSnapshot(ContractObjectState.entities),
            ...this.store.selectSnapshot(UserDefinedObjectState.userDefinedTableDataAsStateEntities)
        };
        let evaluatedStateObjects = this.objectRuleService.validateState(stateEntities, event, this, this.wizardInUse);
        return evaluatedStateObjects.length == 0;
    }

    getAncestorNode(ancestorStateObject: string, thisStateObject: string, thisDataNode: any) {
        let result: any = { Ancestor_Error: `No ancestor relationship found with ${ancestorStateObject} ` };

        let lineID = null;
        let articleID = null;
        let interestID = null;
        let locNum = null;
        let productCode = null;
        let lineCode = null;
        let article: any = {};

        switch (ancestorStateObject.toLowerCase()) {
            case 'procedure':
                result = this.store.selectSnapshot(ContractProcedureState.procedure);
                break;

            case 'contract':
                result = this.store.selectSnapshot(ContractState.contract);
                break;

            case 'consumer':
                result = this.store.selectSnapshot(EntityState.consumer);
                break;

            case 'interest':
                switch (thisStateObject.toLowerCase()) {
                    case 'entity':
                        interestID = thisDataNode.Interest_ID;
                        break;
                }
                if (interestID && interestID != '') {
                    result = this.store.selectSnapshot(InterestState.interestByID)(interestID);
                }
                break;

            case 'line':
                switch (thisStateObject.toLowerCase()) {
                    case 'article':
                    case 'party':
                        lineID = thisDataNode.Line_ID;
                        break;

                    case 'subarticle':
                        articleID = thisDataNode.Article_ID;
                        break;

                    case 'product':
                        articleID = thisDataNode.Article_ID;
                        break;
                }

                if (articleID) {
                    article = this.store.selectSnapshot(ArticleState.articleByID)(articleID);
                    if (article) {
                        lineID = article.Line_ID;
                    }
                }

                if (lineID && lineID != '') {
                    result = this.store.selectSnapshot(LineState.lineByLineID)(lineID);
                }
                break;

            case 'article': // subarticles and products
                if (thisStateObject.toLowerCase() == 'product') {
                    articleID = thisDataNode.Article_ID;

                    if (articleID) {
                        result = this.store.selectSnapshot(ArticleState.articleByID)(articleID);
                    }
                }

                //if nodetype in subarticle types then
                //    find article
                break;

            case 'location': // sublocations, articles, subarticles, and products
                if (thisStateObject == 'Article') {
                    locNum = thisDataNode.Loc_Num;
                    if (locNum && locNum != '') {
                        result = this.store.selectSnapshot(LocationState.locationByLocNum)(locNum);
                    }
                }
                break;

            case 'sublocation': // articles, subarticles, and products
                //if thisStateObject = 'Article'
                //    find loc by locnum
                //if thisStateObject = 'SubArticle'
                //    find article  and then find loc by locnum
                //if thisStateObject =  'Product'
                //    find article  and then find loc by locnum
                break;

            case 'productconfig':
                if (thisStateObject.toLowerCase() == 'product') {
                    productCode = thisDataNode.Product_Code;
                    articleID = thisDataNode.Article_ID;

                    if (articleID) {
                        article = this.store.selectSnapshot(ArticleState.articleByID)(articleID);
                        if (article && article.Line_ID) {
                            lineCode = this.store.selectSnapshot(LineState.lineByLineID)(article.Line_ID).Line_Code;
                            result = this.store.selectSnapshot(ProductLineConfigObjectState.productConfig)(lineCode, productCode);
                        }
                    }
                }
                break;

            case 'service':
                result = {};
                result.CancelProcedure = this.transactionType == TransactionNodeType.Cancel ? 'Y' : 'N';
                break;
        }
        return result;
    }

    setTPBillToEntity(tpEntity) {
        this.store.dispatch([new SetTPBillToEntity(tpEntity)]);
    }

    async openProceduresExist(contractID: string, objectCode: string) {
        return new Promise(async (resolve) => {
            this.dsOpenProcsUnsubscribe();

            this.dsOpenProcs.get(
                {
                    Procedure_Name: 'epx_tempcontractopenprocedures',
                    Contract_ID: contractID,
                    Object_Code: objectCode
                },
                true,
                false
            );

            this.dsOpenProcsStatusSub = this.dsOpenProcs.status$.subscribe((status) => {
                switch (status) {
                    case DataSourceStatus.Ready:
                        this.dsOpenProcsUnsubscribe();

                        if (this.dsOpenProcs.objectNodes.length > 0) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }

                        break;

                    case DataSourceStatus.Error:
                        this.dsOpenProcsUnsubscribe();
                        resolve(false);
                        break;
                }
            });
        });
    }

    async hasOpenProcedures(contractID: string, objectCode: string, procID: string) {
        return new Promise(async (resolve) => {
            this.dsOpenProcsUnsubscribe();

            this.dsOpenProcs.get(
                {
                    Procedure_Name: 'epx_tempcontractsetopenprocedurecheck',
                    Contract_ID: contractID,
                    Object_Code: objectCode,
                    Proc_ID: procID
                },
                true,
                false
            );

            this.dsOpenProcsStatusSub = this.dsOpenProcs.status$.subscribe((status) => {
                switch (status) {
                    case DataSourceStatus.Ready:
                        this.dsOpenProcsUnsubscribe();

                        if (this.dsOpenProcs.objectNodes.length > 0) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }

                        break;

                    case DataSourceStatus.Error:
                        this.dsOpenProcsUnsubscribe();
                        resolve(false);
                        break;
                }
            });
        });
    }

    dsOpenProcsUnsubscribe() {
        if (this.dsOpenProcsStatusSub) {
            this.dsOpenProcsStatusSub.unsubscribe();
        }
    }

    async contractStateValidation(contractID: string, objectCode: string) {
        return new Promise(async (resolve) => {
            this.dsContractStateValidationUnsubscribe();

            this.dsContractStateValidation.get(
                {
                    Procedure_Name: 'epx_contract_state_validation',
                    ContractID: contractID,
                    ObjectCode: objectCode
                },
                true,
                false
            );

            this.dsContractStateValidationSub = this.dsContractStateValidation.status$.subscribe((status) => {
                switch (status) {
                    case DataSourceStatus.Ready:
                        this.dsContractStateValidationUnsubscribe();

                        if (this.dsContractStateValidation.objectNodes.length > 0) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }

                        break;

                    case DataSourceStatus.Error:
                        this.dsContractStateValidationUnsubscribe();
                        resolve(false);
                        break;
                }
            });
        });
    }

    dsContractStateValidationUnsubscribe() {
        if (this.dsContractStateValidationSub) {
            this.dsContractStateValidationSub.unsubscribe();
        }
    }

    selectOpenProcDialog() {
        let component: string = 'SelectOpenProcedureComponent';
        /////////////////////////////////////////////////////////////////////////////////////
        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'lg',
            injector: this.injector,
            backdrop: 'static'
        });
        this.modalRef.componentInstance.showCloseButton = false;
        this.modalRef.componentInstance.type = component;
        this.modalRef.componentInstance.dynamicData = {
            modal: true,
            openProcs: this.dsOpenProcs.objectNodes,
            procedureType: this.transactionType
        };

        /////////////////////////////////////////////////////////////////////////////////////
    }

    closeSelectOpenProcDialog(selected: boolean, procID?: string) {
        if (this.modalRef) {
            this.modalRef.close();
        }

        switch (selected) {
            case true: //Selected Existing Proc.
                this.app.pageParams.setParameter(this.pageID, 'Proc_ID', procID);
                this.pageOS.setPristine();
                this.pageOS.setReadOnlyMode(false);
                this.pageOS.setProtectedMode(false, true);
                this.taskOS.loadTaskHeader(null, ProcObjectCodeType[this.transactionType], procID, this.pageID);
                this.load(this.pageID);
                break;

            case false: //Selected New.
                this.startTemplate();
                break;

            default:
                // Cancelled.
                this.goBack();
                break;
        }
    }

    async hasScheduledCancel(contractID: string) {
        return new Promise(async (resolve) => {
            this.dsOpenProcsUnsubscribe();

            this.dsOpenProcs.get(
                {
                    Procedure_Name: 'epx_getscheduledcancellist',
                    Contract_ID: contractID
                },
                true,
                false
            );

            this.dsOpenProcsStatusSub = this.dsOpenProcs.status$.subscribe((status) => {
                switch (status) {
                    case DataSourceStatus.Ready:
                        this.dsOpenProcsUnsubscribe();

                        if (this.dsOpenProcs.objectNodes.length > 0) {
                            resolve(true);
                        } else {
                            resolve(false);
                        }

                        break;

                    case DataSourceStatus.Error:
                        this.dsOpenProcsUnsubscribe();
                        resolve(false);
                        break;
                }
            });
        });
    }

    openScheduledCancelDialog() {
        let component: string = 'SelectScheduledProcedureComponent';
        /////////////////////////////////////////////////////////////////////////////////////
        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'lg',
            injector: this.injector,
            backdrop: 'static'
        });
        this.modalRef.componentInstance.showCloseButton = false;
        this.modalRef.componentInstance.type = component;
        this.modalRef.componentInstance.dynamicData = {
            modal: true,
            openProcs: this.dsOpenProcs.objectNodes
        };

        /////////////////////////////////////////////////////////////////////////////////////
    }

    closeScheduledCancelDialog(selected: boolean, procedureData?: any) {
        if (this.modalRef) {
            this.modalRef.close();
        }

        switch (selected) {
            case true: //Selected Existing Proc.
                this.templateCriteria = {};
                this.templateCriteria.Contract_Set_ID = procedureData.Contract_Set_ID;
                if (procedureData.Cancel_Date && procedureData.Cancel_Date.indexOf('-00:00') == -1) {
                    procedureData.Cancel_Date += '-00:00';
                }
                this.templateCriteria.Cancel_Date = procedureData.Cancel_Date;
                this.templateCriteria.Cancel_Method = procedureData.Cancel_Method;
                this.scheduledProcData = procedureData;

                this.getTemplate();
                break;

            case false: //Selected New.
                this.openTemplateDialog();
                break;

            default:
                //Cancelled - Go back
                this.goBack();
                break;
        }
    }

    selectLOBDialog() {
        let component: string = 'ContractSelectLOBComponent';
        /////////////////////////////////////////////////////////////////////////////////////
        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'lg',
            injector: this.injector,
            backdrop: 'static'
        });
        this.modalRef.componentInstance.showCloseButton = false;
        this.modalRef.componentInstance.type = component;
        this.modalRef.componentInstance.dynamicData = { modal: true };

        /////////////////////////////////////////////////////////////////////////////////////
    }

    closeSelectLOBDialog(saveConfirmed: boolean, selectedLines: any = []) {
        if (this.modalRef) {
            this.modalRef.close();
        }
        if (saveConfirmed) {
            this.handleContractLineSelection(selectedLines);
        }
    }

    handleContractLineSelection(selectedLines: any) {
        let idx = -1;
        let lineID = 0;
        const currentLines = this.store.selectSnapshot(LineState.lines);
        const currentPrimary = this.store.selectSnapshot(LineState.primaryLine);
        let selectedPrimary: any = {};
        let addList: any = [];
        let deleteList: any = [];

        for (var i in selectedLines) {
            idx = currentLines.map((currentLine) => currentLine.Line_Code).indexOf(selectedLines[i].Line_Code);

            if (idx < 0) {
                addList.push({ Line_Code: selectedLines[i].Line_Code, Line_Name: selectedLines[i].Line_Name });
            }

            if (selectedLines[i].Primary_Ind === true) {
                selectedPrimary = selectedLines[i];
            }
        }

        for (var i in currentLines) {
            idx = selectedLines.map((selectedLine) => selectedLine.Line_Code).indexOf(currentLines[i].Line_Code);

            if (idx < 0) {
                lineID = parseInt(currentLines[i].Line_ID, 10);
                deleteList.push({
                    Line_ID: lineID,
                    Line_Code: currentLines[i].Line_Code,
                    Line_Name: currentLines[i].Line_Code_Desc
                });
            }
        }

        if (addList.length > 0 || deleteList.length > 0 || currentPrimary.Line_Code != selectedPrimary.Line_Code) {
            let summaryString = '';
            if (currentPrimary.Line_Code != selectedPrimary.Line_Code) {
                summaryString =
                    'Change Primary LOB from ' + currentPrimary.Line_Code_Desc + ' to ' + selectedPrimary.Line_Name + '.\n';
            }
            if (addList.length > 0) {
                for (var i in addList) {
                    summaryString += 'Add ' + addList[i].Line_Name + '.\n';
                }
            }
            if (deleteList.length > 0) {
                for (var i in deleteList) {
                    summaryString += 'Remove ' + deleteList[i].Line_Name + '.\n';
                }
            }

            summaryString += '\nProceed with changes?';

            this.confirmationDialog
                .confirm('Confirm Changes', summaryString, 'Yes', 'No', 'lg')
                .then((confirmed) => {
                    if (confirmed) {
                        if (currentPrimary.Line_Code != selectedPrimary.Line_Code) {
                            let fincoParties = this.store.selectSnapshot(PartyState.financeCoParties);
                            let updateFincoParty: any = {};
                            for (var i in fincoParties) {
                                updateFincoParty = { ...fincoParties[i] };
                                updateFincoParty.Line_Code = selectedPrimary.Line_Code;
                                this.updateFinanceCoParty(updateFincoParty);
                            }

                            let updatedContract = { ...this.store.selectSnapshot(ContractState.contract) };
                            updatedContract.Line_Code = selectedPrimary.Line_Code;
                            updatedContract.Program = '';

                            this.updateContract(updatedContract);
                        }
                        if (addList.length > 0) {
                            for (var i in addList) {
                                this.addLine({ Line_Code: addList[i].Line_Code });
                            }
                        }
                        //TODO: Follow up - Move this logic to a change LOB transaction to hand the "auto magic" for party setup.
                        if (deleteList.length > 0) {
                            //Check if Principal Parties belonged to deleted Line(s)
                            let contract = this.store.selectSnapshot(ContractState.contract);
                            let billToEntity = contract.Bill_To_Entity;
                            let billToRoleType = contract.Bill_To_Role_Type;
                            let prinBroker = this.store.selectSnapshot(PartyState.principalBrokerParty);
                            let prinSupplier = this.store.selectSnapshot(PartyState.principalSupplierParty);
                            let prinProducer = this.store.selectSnapshot(PartyState.principalProducerParty);

                            let consumer = this.store.selectSnapshot(EntityState.consumer);
                            let brokers = this.store.selectSnapshot(PartyState.brokerParties);
                            let suppliers = this.store.selectSnapshot(PartyState.supplierParties);
                            let producers = this.store.selectSnapshot(PartyState.producerParties);
                            let index = -1;
                            let updateBroker: any = null;
                            let updateSupplier: any = null;
                            let updateProducer: any = null;
                            let brokerOwnedCN = false;
                            let keepBillToBK = false;

                            if (billToRoleType == 'BK') {
                                for (var i in brokers) {
                                    index = deleteList.map((deleted) => deleted.Line_Code).indexOf(brokers[i].Line_Code);
                                    if (brokers[i].Broker.Entity_ID == billToEntity.Entity_ID && index == -1) {
                                        keepBillToBK = true;
                                        break;
                                    }
                                }
                                if (!keepBillToBK) {
                                    this.store.dispatch(new SetBillToEntity(consumer, 'CN'));
                                }
                            }

                            if (consumer && consumer.Consumer_Role.Broker_Responsible_Ind == 'Y') {
                                brokerOwnedCN = true;
                            }

                            if (prinBroker && prinBroker.Line_Code) {
                                index = deleteList.map((deleted) => deleted.Line_Code).indexOf(prinBroker.Line_Code);
                                if (index > -1) {
                                    for (var i in brokers) {
                                        if (brokers[i].Line_Code == selectedPrimary.Line_Code) {
                                            //Get first broker for Primary LOB and set as Princial.
                                            updateBroker = { ...brokers[i] };
                                            break;
                                        }
                                    }
                                    if (updateBroker != null) {
                                        updateBroker.Principal_Ind = 'Y';
                                        this.updateBrokerParty(updateBroker);
                                    } else {
                                        if (brokerOwnedCN) {
                                            updateBroker = { ...prinBroker };
                                            updateBroker.Line_Code = selectedPrimary.Line_Code;
                                            this.updateBrokerParty(updateBroker);

                                            if (billToRoleType == 'BK' && billToEntity.Entity_ID == updateBroker.Broker.Entity_ID) {
                                                this.store.dispatch(new SetBillToEntity(updateBroker.Broker, 'BK'));
                                            }
                                        }
                                    }
                                }
                            }

                            if (prinSupplier && prinSupplier.Line_Code) {
                                index = deleteList.map((deleted) => deleted.Line_Code).indexOf(prinSupplier.Line_Code);
                                if (index > -1) {
                                    for (var i in suppliers) {
                                        if (suppliers[i].Line_Code == selectedPrimary.Line_Code) {
                                            //Get first supplier for Primary LOB and set as Princial.
                                            updateSupplier = { ...suppliers[i] };
                                            break;
                                        }
                                    }
                                    if (updateSupplier != null) {
                                        updateSupplier.Principal_Ind = 'Y';
                                        this.updateSupplierParty(updateSupplier);
                                    }
                                }
                            }

                            if (prinProducer && prinProducer.Line_Code) {
                                index = deleteList.map((deleted) => deleted.Line_Code).indexOf(prinProducer.Line_Code);
                                if (index > -1) {
                                    for (var i in producers) {
                                        if (producers[i].Line_Code == selectedPrimary.Line_Code) {
                                            //Get first supplier for Primary LOB and set as Princial.
                                            updateProducer = { ...producers[i] };
                                            break;
                                        }
                                    }
                                    if (updateProducer != null) {
                                        updateProducer.Principal_Ind = 'Y';
                                        this.updateProducerParty(updateProducer);
                                    }
                                }
                            }

                            for (var i in deleteList) {
                                this.deleteLine(deleteList[i].Line_ID);
                            }
                        }

                        this.sectionsBuilt = false;
                        this.lobChanged = true;
                        this.process(EventType.Update);
                    }
                })
                .catch(() => { });
        }
    }

    selectLineWideProductDialog(costType: CostType) {
        let component: string = 'SelectLineWideProductComponent';
        /////////////////////////////////////////////////////////////////////////////////////
        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'sm',
            injector: this.injector,
            backdrop: 'static'
        });
        this.modalRef.componentInstance.showCloseButton = false;
        this.modalRef.componentInstance.type = component;
        this.modalRef.componentInstance.dynamicData = {
            modal: true,
            costType: costType
        };

        /////////////////////////////////////////////////////////////////////////////////////
    }

    closeSelectLineWideProductDialog(data: any = {}) {
        if (this.modalRef) {
            this.modalRef.close();
        }
        if (
            data.State &&
            data.State != '' &&
            data.Line_Code &&
            data.Line_Code != '' &&
            data.Product_Code &&
            data.Product_Code != ''
        ) {
            this.addLineWideProduct(data.Line_Code, data.State, data.Product_Code);
        }
    }

    async addLineWideProduct(lineCode: string, state: string, productCode: string) {
        let stateLocs: any = [];
        let articleID = '';
        let lineID = '';
        let lineWideArticles: any = [];
        let currentLocations = this.store.selectSnapshot(LocationState.locations).sort((a, b) => a.Loc_Num - b.Loc_Num);

        const currentLines = this.store.selectSnapshot(LineState.lines);
        for (var line in currentLines) {
            if (currentLines[line].Line_Code == lineCode) {
                lineID = currentLines[line].Line_ID;
                break;
            }
        }

        for (var loc in currentLocations) {
            if (currentLocations[loc].State == state && !['D', 'X'].includes(currentLocations[loc].Edit_Status)) {
                stateLocs.push(currentLocations[loc].Loc_Num);
            }
        }

        if (stateLocs.length == 0) {
            //Add new Location. Skip checking for existing articles, 
            // as it wouldn't be possible to have one for the State if the Location had to be added.
            await this.autoAddLocForLineWideArticle({ State: state }); //Adding should set the new location as the current location.
            let currentLocation = this.store.selectSnapshot(LocationState.currentLocation);
            stateLocs.push(currentLocation.Loc_Num);
        } else {
            //Existing Article check.
            lineWideArticles = this.store.selectSnapshot(ArticleState.articlesByLineAndType)(
                lineID,
                ArticleNodeType.Line_Wide
            );

            //Loop though all the state locations, looking for the first LineWide Article to reference one of the state locations.
            if (lineWideArticles.length > 0) {
                locationLoop: for (var sLoc in stateLocs) {
                    for (var article in lineWideArticles) {
                        if (
                            lineWideArticles[article].Loc_Num == stateLocs[sLoc] &&
                            !['D', 'X'].includes(lineWideArticles[article].Edit_Status)
                        ) {
                            articleID = lineWideArticles[article].Article_ID;
                            break locationLoop;
                        }
                    }
                }
            }
        }

        if (!articleID || articleID == '') {
            // No article found that uses a Location w/in selected State. Use first Location that references the State for new Article.
            //Adding should set the new article as the current article.
            await this.autoAddLineWideArticle(lineID, ArticleNodeType.Line_Wide, { Loc_Num: stateLocs[0] });
            lineWideArticles = this.store.selectSnapshot(ArticleState.articlesByLineAndType)(
                lineID,
                ArticleNodeType.Line_Wide
            );

            let currentArticle = this.store.selectSnapshot(ArticleState.currentArticle);
            articleID = currentArticle.Article_ID;
        }

        if (articleID && articleID != '') {
            let product: any = {};
            let productConfig = this.store.selectSnapshot(ProductLineConfigObjectState.productConfig)(lineCode, productCode);

            if (productConfig && productConfig.Product_Code) {
                product.Product_Code = productConfig.Product_Code;
                product.Product_Code_Desc = productConfig.Product_Desc;
                product.Product_Cost_Type = productConfig.Product_Cost_Type;
                product.Display_Order = productConfig.Display_Order;
                product.Fully_Earned_Ind = productConfig.Fully_Earned_Ind;
                product.Net_Change_Method = productConfig.Net_Change_Method;
                product.Reporting_Fully_Earned_Ind = productConfig.Reporting_Fully_Earned_Ind;

                this.addProductsToArticle(parseInt(articleID, 10), [product]);
            }
        }
    }

    //Special Case to force synchronicity. Do not use as a pattern unless synchronicity is required.
    private autoAddLocForLineWideArticle(locationTemplate: any = {}) {
        return new Promise(async (resolve) => {
            this.store.dispatch(new AddLocation(locationTemplate)).subscribe(() => {
                resolve(true);
            });
        });
    }

    //Special Case to force synchronicity. Do not use as a pattern unless synchronicity is required.
    private autoAddLineWideArticle(lineID: string, articleType: ArticleNodeType, article: any = {}) {
        return new Promise(async (resolve) => {
            this.store.dispatch(new AddArticle(lineID, articleType, article)).subscribe(() => {
                this.autoDisplayProductSets();
                resolve(true);
            });
        });
    }

    selectProductDialog(article: any, lineCode) {
        let component: string = 'ContractSelectProductComponent';
        /////////////////////////////////////////////////////////////////////////////////////
        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'lg',
            injector: this.injector,
            backdrop: 'static',
            windowClass: 'ep-modal-panel-double'
        });
        this.modalRef.componentInstance.showCloseButton = false;
        this.modalRef.componentInstance.type = component;
        this.modalRef.componentInstance.dynamicData = { modal: true, article: article, lineCode: lineCode };

        /////////////////////////////////////////////////////////////////////////////////////
    }

    closeSelectProductDialog(products?: any, article?: any) {
        if (this.modalRef) {
            this.modalRef.close();
        }
        if (article && article.Article_ID && products && products.length > 0) {
            this.handleSelectedProducts(products, article);
        }
    }

    handleSelectedProducts(products: any, article: any) {
        let articleID = +article.Article_ID;
        let newProducts: any = [];
        let updatedProducts: any = [];
        products = cloneDeep(products);

        for (var p in products) {
            if (!products[p].isNew && !products[p].isChanged) {
                continue;
            }
            if (products[p].isNew) {
                delete products[p].isNew; //Delete these boolean properties before adding to product state.
                delete products[p].isChanged;
                newProducts.push(products[p]);
            }
            if (products[p].isChanged) {
                delete products[p].isChanged; //Delete these boolean properties before changing record in product state.
                delete products[p].isNew;
                updatedProducts.push(products[p]);
            }
        }

        if (newProducts.length > 0) {
            this.addProductsToArticle(articleID, newProducts);
        }

        if (updatedProducts.length > 0) {
            this.pageOS.setDirty();
            for (var p in updatedProducts) {
                let newData = updatedProducts[p].New_Data.product;
                delete updatedProducts[p].New_Data;
                let updateProduct = { ...updatedProducts[p], ...newData };

                //Remove Default, Mandatory, Optional flags if not added/updated via AutoDisplay products.
                delete updateProduct.Optional_Ind;
                delete updateProduct.Default_Ind;
                delete updateProduct.Mandatory_Ind;

                this.updateProduct(updateProduct);
            }
        }
    }

    async loadAndValidate(objectID: any, event: PageEventType) {
        return new Promise(async (resolve) => {
            this.dsGetLoadValidate = this.dataService.addNewDatasource(this.transactionType);
            if (this.dsGetLoadValidateStatusSub) {
                this.dsGetLoadValidateStatusSub.unsubscribe();
            }

            if (this.wizardInUse) {
                this.dsGetLoadValidate.get({ Proc_ID: objectID, View: 'Full', json: 'CT2' }, true, false);
                this.dsGetLoadValidateStatusSub = this.dsGetLoadValidate.status$.subscribe((status) => {
                    switch (status) {
                        case DataSourceStatus.Ready:
                            if (this.dsGetLoadValidateStatusSub) {
                                this.dsGetLoadValidateStatusSub.unsubscribe();
                            }

                            let normalizedData = normalize(this.dsGetLoadValidate.data, rootSchema);

                            this.store.dispatch(new SetContractObjectState(normalizedData.entities)).subscribe((action) => {
                                let valid = this.validate(event);
                                if (this.dsGetLoadValidate) {
                                    this.dataService.deleteDatasource(this.dsGetLoadValidate);
                                }

                                resolve(valid);
                            });
                            break;
                        case DataSourceStatus.Error:
                            if (this.dsGetLoadValidateStatusSub) {
                                this.dsGetLoadValidateStatusSub.unsubscribe();
                            }
                            if (this.dsGetLoadValidate) {
                                this.dataService.deleteDatasource(this.dsGetLoadValidate);
                            }
                            resolve(false);
                            break;
                    }
                });
            } else {
                if (this.dsGetLoadValidate) {
                    this.dataService.deleteDatasource(this.dsGetLoadValidate);
                }
                resolve(false);
            }
        });
    }

    autoDisplayProductSets(articleID?: any) {
        if (this.app.DesignerMode) {
            return;
        }
        if (this.dsProductSetSub) {
            this.dsProductSetSub.unsubscribe();
        }
        this.dsProductSet.reset();

        if (!articleID) {
            articleID = this.store.selectSnapshot(ArticleState.currentArticle).Article_ID;
        }
        if (articleID) {
            let criteria = cloneDeep(this.store.selectSnapshot(ContractObjectState.productSetCriteria)(articleID));
            criteria.Procedure_Name = 'epx_getproductsets';
            criteria.Auto_Display_Ind = 'Y';

            this.dsProductSet.get(criteria, true, false);

            this.dsProductSetSub = this.dsProductSet.status$.subscribe((status) => {
                this.autoDisplayProductSetsSub(status, articleID);
            });
        }
    }

    private autoDisplayProductSetsSub(status: DataSourceStatus, articleID: any) {
        switch (status) {
            case DataSourceStatus.Ready:
                let newProducts: any = [];
                if (this.dsProductSetSub) {
                    this.dsProductSetSub.unsubscribe();
                }
                if (this.dsProductSet && this.dsProductSet.objectNodes.length > 0) {
                    for (var s in this.dsProductSet.objectNodes) {
                        if (this.dsProductSet.objectNodes[s].Products && this.dsProductSet.objectNodes[s].Products.Product) {
                            let products = this.dsProductSet.objectNodes[s].Products.Product;
                            if (Array.isArray(products)) {
                                for (var p in products) {
                                    newProducts.push(products[p]);
                                }
                            } else {
                                newProducts.push(products);
                            }
                        }
                    }

                    let articleIDNum = +articleID;
                    if (newProducts.length > 0) {
                        this.addProductsToArticle(articleIDNum, newProducts, true);
                    }
                }
                break;
            case DataSourceStatus.Error:
                if (this.dsProductSetSub) {
                    this.dsProductSetSub.unsubscribe();
                }

                break;
        }
    }

    autoSaveContract(event: EventType) {
        try {
            this.process(event);
            return true;
        } catch (e) {
            console.log(e);
            return false;
        }
    }

    async beginRating() {
        this.launchRating = false;
        await this.ratingService.loadRatingObjects(this.store.selectSnapshot(ContractState.contract).Contract_ID);

        if (this.ratingService.ratingObjectsCount === 0) {
            this.app.showAlert({
                title: 'Warning',
                message: 'No rating configurations were found. Please contact your system administrator.'
            });
        } else if (this.ratingService.ratingObjectsCount === 1) {
            let ratingObjects = this.ratingService.ratingObjects;
            this.rate(ratingObjects[0]);
        } else {
            this.selectRatingObjectDialog();
        }
    }

    rate(ratingObject: any) {
        let supplierCount = this.store.selectSnapshot(PartyState.supplierPartyCount);
        let rateContractID = this.store.selectSnapshot(ContractState.currentID);
        let rateEntityID = this.store.selectSnapshot(EntityState.currentConsumerID);

        if (
            ratingObject.object_class?.toLowerCase() == 'x' &&
            (ratingObject.object_code == 'SSRating' || ratingObject.object_code == 'SSRatingBB')
        ) {
            if (supplierCount === 0) {
                this.app.showAlert({
                    title: 'Warning',
                    message: 'A supplier must be selected before Spreadsheet Rating can be processed.'
                });
            } else {
                const body = {
                    '?xml': { '@version': '1.0' },
                    ConceptOneRequest: {
                        SpreadsheetRatingProcess: {
                            '@Event': ratingObject.object_code == 'SSRating' ? 'Load' : 'Rate', //call rate event if using blacbox rating
                            '@Book_Entity_ID': this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID,
                            '@Lock_GUID': this.pageOS.getCurrentLockGUID(),
                            SpreadsheetRating: {
                                Contract_ID: rateContractID,
                                Line_Code: ratingObject.line_code
                            }
                        }
                    }
                };
                if (this.dsProcessRatingStatusSub) {
                    this.dsProcessRatingStatusSub.unsubscribe();
                }
                this.dsProcessRating.reset();
                //this.sessionIdle.pauseTimer();

                this.dsProcessRatingStatusSub = this.dsProcessRating.status$.subscribe((status) => {
                    if (ratingObject.object_code == 'SSRating') {
                        this.ratingLoaded(status, ratingObject);
                    } else {
                        //this.sessionIdle.resumeTimer();
                        this.reloadObject();
                    }
                });

                this.dsProcessRating.process({ json: 'CT2' }, body);
                if (ratingObject.object_code == 'SSRating') {
                    this.ratingOpenDialog();
                }
            }
        } else if (
            ratingObject.object_class?.toLowerCase() == 'x' &&
            (ratingObject.set_values?.toLowerCase().startsWith('[webservice]') ||
                ratingObject.set_values?.toLowerCase().startsWith('[website]'))
        ) {
            let interfaceParms: any = {
                Object_Code: ratingObject.object_code,
                Entity_ID: rateEntityID,
                Role_Type: 'CN',
                Contract_ID: rateContractID,
                Contract_Lock_GUID: this.pageOS.getCurrentLockGUID()
            };

            this.router.navigate(['interface', interfaceParms]);

            this.interfaceCompleteSub = this.interfaceOS.interfaceComplete.subscribe((status) => {
                this.interfaceCompleted(status);
            });
        }
    }

    ratingLoaded(status: DataSourceStatus, ratingObject: any) {
        switch (status) {
            case DataSourceStatus.Ready:
                if (this.dsProcessRatingStatusSub) {
                    this.dsProcessRatingStatusSub.unsubscribe();
                }

                // let nav = ratingObject.set_values;
                // nav.replace('%ContentServerURL%', this.baseUrl);
                // nav.replace('%wsguid%', this.dsProcessSSRating.objectNode.Workspace_GUID);

                var baseUrl = "";

                let queryParms = 'cs=' + baseUrl + '&wsguid=' + this.dsProcessRating.objectNode.Workspace_GUID;
                if (this.app.CurrentSystemCode.length > 0) {
                    //override the base system tied to the ETM Service Route
                    queryParms += '&system=' + this.app.CurrentSystemCode;
                }

                let nav = '/g2spreadsheetrating:' + btoa(queryParms);

                this.router.navigate([nav, {}]);
                break;
            case DataSourceStatus.Error:
                if (this.dsProcessRatingStatusSub) {
                    this.dsProcessRatingStatusSub.unsubscribe();
                }
                break;
        }
    }

    ratingOpenDialog() {
        let component: string = 'RatingOpenComponent';
        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'md',
            injector: this.injector,
            backdrop: 'static'
        });
        this.modalRef.componentInstance.showCloseButton = false;
        this.modalRef.componentInstance.type = component;
        this.modalRef.componentInstance.dynamicData = {};
    }

    closeRatingOpenDialog() {
        let criteria: any = { View: 'Full', json: 'CT2', Workspace_Key: this.dsProcessRating.objectNode.Workspace_GUID };

        this.dsGetRatingWorkspace.get(criteria, true, false);

        this.dsGetRatingWorkspaceStatusSub = this.dsGetRatingWorkspace.status$.subscribe((x) => {
            this.dsGetRatingWorkspaceStatusUpdated(x);
        });
    }

    dsGetRatingWorkspaceStatusUpdated(status: DataSourceStatus) {
        switch (status) {
            case DataSourceStatus.Ready:
                if (this.dsGetRatingWorkspaceStatusSub) {
                    this.dsGetRatingWorkspaceStatusSub.unsubscribe();
                }
                //check workspace status
                switch (this.dsGetRatingWorkspace.data.Root.Status) {
                    case 'Rated':
                    case 'Canceled':
                        if (this.modalRef) {
                            this.modalRef.close();
                        }
                        this.completeRating(this.dsProcessRating.objectNode.Workspace_GUID);
                        //this.sessionIdle.resumeTimer();
                        this.loadObject(this.objectID);
                        break;
                    default:
                        this.confirmationDialog
                            .confirm(
                                'Complete Spreadsheet Rating?',
                                'It seems that the Spreadsheet Rating desktop application is still open. Do you wish to complete Spreadsheet Rating?',
                                'Complete',
                                'Cancel',
                                'lg'
                            )
                            .then((confirmed) => {
                                if (confirmed) {
                                    if (this.modalRef) {
                                        this.modalRef.close();
                                    }
                                    this.completeRating(this.dsProcessRating.objectNode.Workspace_GUID);
                                }
                            })
                            .catch(() => { })
                            .finally(() => { return; /*this.sessionIdle.resumeTimer()*/ });
                        break;
                }

                break;
            case DataSourceStatus.Error:
                if (this.dsGetRatingWorkspaceStatusSub) {
                    this.dsGetRatingWorkspaceStatusSub.unsubscribe();
                }
                if (this.modalRef) {
                    this.modalRef.close();
                }
                //this.sessionIdle.resumeTimer();
                this.loadObject(this.objectID);
                break;
        }
    }

    selectRatingObjectDialog() {
        let component: string = 'SelectRatingObjectComponent';

        this.modalRef = this.modalService.open(DynamicContentModalComponent, {
            centered: true,
            size: 'lg',
            injector: this.injector,
            backdrop: 'static'
        });
        this.modalRef.componentInstance.showCloseButton = false;
        this.modalRef.componentInstance.type = component;
        this.modalRef.componentInstance.dynamicData = {
            ratingObjects: this.ratingService.ratingObjects
        };
    }

    closeSelectRatingObjectDialog(selected: boolean, ratingObject?: any) {
        if (this.modalRef) {
            this.modalRef.close();
        }

        switch (selected) {
            case true:
                this.rate(ratingObject);
                break;

            default:
                // Cancelled.
                break;
        }
    }

    completeRating(workspaceGUID: string) {
        const body = {
            '?xml': { '@version': '1.0' },
            ConceptOneRequest: {
                SpreadsheetRatingProcess: {
                    '@Event': 'Complete',
                    '@Book_Entity_ID': this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID,
                    SpreadsheetRating: {
                        '@Workspace_GUID': workspaceGUID
                    }
                }
            }
        };
        if (this.dsProcessRatingStatusSub) {
            this.dsProcessRatingStatusSub.unsubscribe();
        }
        this.dsProcessRating.reset();

        this.dsProcessRating.process({ json: 'CT2' }, body);
    }

    interfaceCompleted(status) {
        if (this.interfaceCompleteSub) {
            this.interfaceCompleteSub.unsubscribe();
        }
        if (status === 'COMPLETE') {
            this.loadObject(this.objectID);
        }
    }

    public locationDeleteRelatedArticlesConfirmDelete(locNum: string): Observable<boolean> {
        let contractObjectArticles$;
        contractObjectArticles$ = this.store.select(ContractObjectState.articlesReferencingLocNum).pipe(
            map((fn) => fn(locNum)),
            takeUntil(this.LocationDeleteRelatedArticlesUnsubscribe$)
        );

        return contractObjectArticles$.pipe(
            take(1),
            switchMap((articles: any[]) => {
                if (!articles || articles.length === 0) {
                    // No associated articles, so proceed with location delete
                    return of(true);
                } else {
                    return this.openLocationDeleteRelatedArticlesDialog(articles).pipe(take(1));
                }
            })
        );
    }

    public openLocationDeleteRelatedArticlesDialog(articles: any[]): Observable<boolean> {
        let dialogComponentRef;
        dialogComponentRef = LocationDeleteShowRelatedArticlesDialogComponent;

        const modalRef: NgbModalRef = this.modalService.open(dialogComponentRef, {
            centered: true,
            size: 'lg',
            backdrop: 'static',
            windowClass: 'ep-modal-search-dialog',
            keyboard: false
        });

        modalRef.componentInstance.articles = articles;

        return from(modalRef.result).pipe(
            map((result: boolean) => of(result)),
            catchError((error) => of(error)),
            finalize(() => {
                dialogComponentRef = null;
                this.LocationDeleteRelatedArticlesUnsubscribe$.next(true);
            })
        );
    }

    private async designerInitalizeForceSyncAdd(
        //This should only ever be called when initalizing data for the UX desinger.
        recordType: 'Location' | 'Line' | 'PrimaryLine',
        lineCode: any = '',
        lineID: any = ''
    ) {
        return new Promise<void>(async (resolve) => {
            switch (recordType) {
                case 'Location':
                    this.store.dispatch(new AddLocation({ Loc_Num: '1' })).subscribe((action) => {
                        resolve();
                    });
                    break;

                case 'Line':
                    this.store.dispatch(new AddLine({ Line_Code: lineCode })).subscribe((action) => {
                        resolve();
                    });
                    break;
                case 'PrimaryLine':
                    this.store.dispatch(new SetPrimaryLine({ Line_ID: lineID, Line_Code: lineCode })).subscribe((action) => {
                        resolve();
                    });
                    break;
            }
        });
    }

    /* validates contract and party values such as programs. The same function exists in the SDK
    and this mimics that functionality to avoid calling the SDK transaction in case of validation errors
    */
    private validateContractSetup(event: string) {
        let errorMsg: string = '';
        let brokers = this.store.selectSnapshot(PartyState.brokerParties);
        let suppliers = this.store.selectSnapshot(PartyState.supplierParties);
        let producers = this.store.selectSnapshot(PartyState.producerParties);
        let contract = this.store.selectSnapshot(ContractState.contract);
        let lines = this.store.selectSnapshot(LineState.lines);
        //mimics SDK logic for validating parties to prevent constructing a bad SDK request
        if (
            !(
                this.transactionType == TransactionNodeType.Application ||
                this.transactionType == TransactionNodeType.Contract ||
                (this.transactionType == TransactionNodeType.Renewal &&
                    this.app.getBookOptionValue('Renew_As_App_Ind') == 'Y') ||
                event == EventType.Update
            )
        ) {
            const supplierExistsPerLine = this.supplierPartyExistsPerLine(suppliers, lines);
            if (!supplierExistsPerLine) {
                errorMsg +=
                    'Invalid Supplier Party. A Supplier Party is required for each line. Please review Supplier party record(s).';
                return errorMsg;
            }

            const brokerExistsPerLine = this.brokerPartyExistsPerLine(brokers, lines);
            if (!brokerExistsPerLine) {
                errorMsg +=
                    'Invalid Broker Party. A Broker Party is required for each line. Please review Broker party record(s).';
                return errorMsg;
            }

            let partiesWithoutAgmt = this.store.selectSnapshot(PartyState.partiesWithoutAgmt);
            if (partiesWithoutAgmt.length > 0) {
                errorMsg +=
                    'Invalid Contract Party Agreement(s). Please review/re-select Contract Broker or Supplier party record(s).';
                return errorMsg;
            }
        }

        if (brokers?.length > 0) {
            const isPercentageValid = this.isPartyPercentageValid(brokers, 'Broker');
            if (!isPercentageValid) {
                errorMsg +=
                    'Invalid Broker Participation Percentage(s). Credit % must equal 100.00 for each Line, Program and State combination.';
            }
        }

        if (suppliers?.length > 0) {
            const isPercentageValid = this.isPartyPercentageValid(suppliers, 'Supplier');
            if (!isPercentageValid) {
                errorMsg +=
                    'Invalid Supplier Risk Group Share Percentage(s). ' +
                    'Share % must equal 100.00 for each Line, Program, State and Risk Group combination.';
            }
        }

        if (brokers?.length > 0 || suppliers?.length > 0 || producers?.length > 0) {
            if (contract && contract.Program != '' && contract.Program != null) {
                for (var i in suppliers) {
                    if (suppliers[i]) {
                        if (suppliers[i].Line_Code == contract.Line_Code) {
                            if (
                                suppliers[i].Program != contract.Program ||
                                suppliers[i].Program == '' ||
                                suppliers[i].Program == null
                            ) {
                                errorMsg += 'Programs on Supplier record(s) do not match program on contract.';
                                break;
                            }
                        }
                    }
                }
                for (var i in brokers) {
                    if (brokers[i]) {
                        if (brokers[i].Line_Code == contract.Line_Code) {
                            if (brokers[i].Program != contract.Program || brokers[i].Program == '' || brokers[i].Program == null) {
                                errorMsg += 'Programs on Broker record(s) do not match program on contract.';
                                break;
                            }
                        }
                    }
                }
                for (var i in producers) {
                    if (producers[i]) {
                        if (producers[i].Line_Code == contract.Line_Code) {
                            if (
                                producers[i].Program != contract.Program ||
                                producers[i].Program == '' ||
                                producers[i].Program == null
                            ) {
                                errorMsg += 'Programs on Producer record(s) do not match program on contract.';
                                break;
                            }
                        }
                    }
                }
            }
        }
        return errorMsg;
    }

    private brokerPartyExistsPerLine(brokers: any, lines: any) {
        if (brokers && brokers.length === 0) {
            return true;
        }

        let brokerExistsPerLine = false;
        let lineCodes: string[] = [];
        for (var i in lines) {
            if (lines[i].Edit_Status != EditStatusType.Delete) {
                lineCodes.push(lines[i].Line_Code);
            }
        }

        lineCodes.sort((a, b) => a.localeCompare(b));

        const currentBrokers: any[] = brokers.filter(
            (party) =>
                party.Edit_Status != EditStatusType.Delete &&
                party.Broker &&
                party.Broker.Entity_ID &&
                party.Broker.Entity_ID !== ''
        );

        const uniqueBrokers = new Set(currentBrokers.map((party) => party.Line_Code));
        const brokersWithLines = Array.from(uniqueBrokers).sort((a, b) => a.localeCompare(b));

        if (lineCodes.length === brokersWithLines.length) {
            brokerExistsPerLine = lineCodes.every((v, i) => v === brokersWithLines[i]);
        }

        return brokerExistsPerLine;
    }

    private supplierPartyExistsPerLine(suppliers: any, lines: any) {
        let supplierExistsPerLine = false;

        let lineCodes: string[] = [];
        for (var i in lines) {
            if (lines[i].Edit_Status != EditStatusType.Delete) {
                lineCodes.push(lines[i].Line_Code);
            }
        }

        lineCodes.sort((a, b) => a.localeCompare(b));

        const currentSuppliers: any[] = suppliers.filter(
            (party) =>
                party.Edit_Status != EditStatusType.Delete &&
                ((party.Source_Supplier && party.Source_Supplier.Entity_ID && party.Source_Supplier.Entity_ID !== '') ||
                    (party.Risk_Supplier && party.Risk_Supplier.Entity_ID && party.Risk_Supplier.Entity_ID !== ''))
        );

        const uniqueSuppliers = new Set(currentSuppliers.map((party) => party.Line_Code));
        const suppliersWithLines = Array.from(uniqueSuppliers).sort((a, b) => a.localeCompare(b));

        if (lineCodes.length === suppliersWithLines.length) {
            supplierExistsPerLine = lineCodes.every((v, i) => v === suppliersWithLines[i]);
        }

        return supplierExistsPerLine;
    }

    private isPartyPercentageValid(partiesList: any[], partyType: string) {
        let partyPercentagesValid = true;
        let groupByProperties = [];
        let sumProperty = '';
        let totalGroupPct = 0;

        switch (partyType) {
            case 'Broker':
                sumProperty = 'Participation_Pct';
                groupByProperties = ['Line_Code', 'Program', 'State'];
                break;
            case 'Supplier':
                sumProperty = 'RiskGroup_Share_Pct';
                groupByProperties = ['Line_Code', 'Program', 'State', 'RiskGroup_Code'];
                break;
            default:
                break;
        }

        const partyByGroup = groupBy(
            [...groupByProperties],
            partiesList.filter((party) => party.Edit_Status != EditStatusType.Delete)
        );

        for (var i in partyByGroup) {
            if (partyPercentagesValid) {
                totalGroupPct = this.sumByProperties(partyByGroup[i], sumProperty);
                if (!isNaN(totalGroupPct)) {
                    const fixedTotal = Number(totalGroupPct).toFixed(4);
                    if (fixedTotal !== '100.0000') {
                        partyPercentagesValid = false;
                    }
                } else {
                    partyPercentagesValid = false;
                }
            }
        }

        return partyPercentagesValid;
    }

    private sumByProperties(items: any, sumProperty: string) {
        let total: number = 0.0;
        for (var i in items) {
            let item = items[i];
            if (item.hasOwnProperty(sumProperty)) {
                let value = item[sumProperty];
                if (value === null || value === undefined || value === '') {
                    value = 0.0;
                } else if (typeof value === 'string') {
                    value = Number(value).toFixed(4);
                } else {
                    value = value.toFixed(4);
                }
                total += Number(value);
            }
        }
        return total;
    }
}
